/**
 * @file src/room/roomClient.js
 * @description The room-mode client: joins the relay room this realm was opened with, keeps the
 * room store current (phase / role / grants / peers), publishes this player's edits as addressed
 * player events, and applies the other players' stamped events to the local voices.
 *
 * One client per tab, riding the SAME Connect socket the sync adapter owns (its raw-message tee +
 * `sendRaw`) — one tab stays one relay peer. Everything here is room-mode only: the client is only
 * constructed when the multi boot carries a room, so the solo / plain-multi paths never see it.
 *
 * Ownership model (client side = courtesy, the relay is the gate):
 *  - Local edits publish only for voices this client is granted, and only while playing.
 *  - Incoming events apply to the addressed voice through its own ParameterManager, tagged with a
 *    remote source so they can never echo back out.
 *  - Voices this client does NOT hold get a pointer-blocking cover (and a soft parameter write
 *    gate) so nothing on them is live-but-local.
 *  - Within a voice several people may hold at once, so one CONTROL at a time goes to whoever
 *    reached it first: the ordered stream is folded into `roomLeases` (see paramLease.js) and both
 *    the write gate and the control's own lock read that answer.
 */

import { voiceRegistry } from '../voice/VoiceRegistry.js';
import { SensorController } from '../input/SensorsController.js';
import { isGeneratorSource } from '../input/source/InputRouter.ts';
import { syncCoordinator } from '../sync/SyncCoordinator.js';
import { DEFAULT_LAUNCH_GRID_BARS } from '../sync/launchGrid.js';
import { SYNC_BPM_DISPLAY_SOURCE } from '../audio/syncBpmDisplaySource.js';
import { INPUT_GAIN_PARAM, PREMIX_PARAM } from '../audio/gainStageParams.js';
import { roomStore } from './roomStore.js';
import { isMatchmakingArrival } from './roomGrant.js';
import { roomLeases } from './roomLeases.js';
import { roomScore } from './roomScore.js';
import { normalizeDrawPinned } from './handDraw.js';
import { getRoomClientId, resolveDisplayName, adoptRoomIdentity } from './identity.js';
import {
  createEnvelopeStamper,
  createSeqTracker,
  parseAddress,
  paramAddress,
  NO_DIMENSION,
  dimensionAddress,
  transportAddress,
  loopEnableAddress,
  loopRangeAddress,
  loopSizeAddress,
  nativeTempoAddress,
  downbeatAddress,
  launchGridAddress,
  seekAddress,
  warpAddress,
  syncAddress,
  panelAddress,
  infoPanelAddress,
  cameraAddress,
  gainChannelAddress,
  cosmicAddress,
  sensorAddress,
  loadAddress,
  randomMsgId,
  delayMsToBeat,
  grantsFromWire,
  grantHolders,
} from './wire.js';
import { getCameraFocus, setCameraFocus, subscribeCameraFocus } from '../world/cameraFocus.js';
import { createChatClient } from './chatClient.js';
import { SessionOutcome } from './sessionOutcome.js';
import {
  openChatPanelWithoutRemembering,
  resetChatPanel,
  restoreChatPanelFromMemory,
  setChatPanelOpen,
} from './chat/chatPanelState.js';
import { applyRemoteDeckLoad, applyRemoteDeckClear, applyRemoteCardPlayed, deckContents, deckIsMaterializing, handConsumed, applyRemoteHandConsumed, setRoomDeckHandlers } from './roomDeckBridge.js';
import {
  applyRemoteSensor as applyRemoteSensorToDeck,
  applySensorSnapshot,
  syncDeviceToSwitch,
  setRoomSensorPublishers,
  voiceSensorSnapshot,
} from '../input/sensorSwitches.js';
import {
  applyRemotePanel,
  applyRemoteInfo,
  applyRemoteLoopSize,
  applyRemoteGainChannel,
  forgetVoiceViewState,
  setRoomPanelPublishers,
  voicePanelSnapshot,
} from './roomPanelBridge.js';

/**
 * THE routed-parameter table — the one answer to "is this per-voice control routed, and how?".
 * Keyed by ParameterManager param name; the write gate, the attach/detach subscribe loops, the
 * local publish fork, both snapshot sides and the remote apply all read this table (or one of the
 * projections derived right below it) — never a parallel list. Every PM param NOT in the table is
 * covered by the write gate's catch-all + the pointer covers; everything else the event layer
 * carries (transport, loops, warp/sync, deck fields, panels, sensors, the Cosmic LFO's discrete
 * controls) rides its own address kind and is not a PM param at all.
 *
 * Fields:
 *  - `dimensional` — decides BOTH the publish path and the lease scope.
 *    `true`: carries a dimension. Publishes per-dimension on the axis-param path, snapshots under
 *    `parameters.dims[dimId][name]`, and its lease is arbitrated at the WRITE's own dimension.
 *    `false` (the gain stages): a single param on its own path, kept away from the per-voice
 *    dimension dedup (`#lastPublishedDim`) — an interleaved axis dimension switch would dedup
 *    against a fader move and misfire. Snapshots as one flat `parameters[stateField]` level; the
 *    lease is arbitrated at the param's own scope. Both stages route because the shared mix must
 *    converge — a holder's move is heard and seen everywhere (only the device's own master output
 *    volume stays local).
 *  - `axis` — one of the three axes themselves: exactly what a dimension-switch bundle carries.
 *    The Cosmic LFO knobs stay out of the bundle because it is about the axes, not the tools
 *    driving them.
 *  - `cosmicKnob` — a Cosmic LFO knob (depth / manual frequency). Registered exactly like the axes
 *    (multidimensional, per-dimension values) so it routes with no special handling — but a
 *    snapshot re-applies these LAST, after the LFO's source pick has recomputed the frequency.
 *  - `stateField` — the flat `player:state` field for a non-dimensional param. `premix` keeps the
 *    field name the snapshot has always used, so an older peer's stored snapshot still restores
 *    the fader it knows.
 *
 * One write-gate rule covers every entry: hold the voice (a grant, or a gesture opened before a
 * section revocation), then win the lease at the scope `dimensional` names above.
 *
 * Why one table: each shape this replaced shipped its own freeze. The Cosmic knobs were registered
 * like the axes but missing from the routed set, so the gate's catch-all refused them — in a room
 * both knobs did nothing, for everybody, while the same knobs worked solo. And the premix fader
 * was routed while the input trim was not, so the gain rail's IN side was frozen while its OUT
 * side moved the whole room. The gain names come from `gainStageParams.js` (persisted ids — never
 * rename); a stage added there needs a row here, or the gate freezes it in rooms all over again.
 */
const ROUTED_PARAMS = new Map([
  ['x', { dimensional: true, axis: true }],
  ['y', { dimensional: true, axis: true }],
  ['z', { dimensional: true, axis: true }],
  ...['x', 'y', 'z'].flatMap((axis) => [
    [`${axis}-cosmic-amplitude`, { dimensional: true, cosmicKnob: true }],
    [`${axis}-cosmic-frequency`, { dimensional: true, cosmicKnob: true }],
  ]),
  // In signal order (input first), matching `GAIN_STAGE_PARAMS` — the order the loops publish in.
  [INPUT_GAIN_PARAM, { dimensional: false, stateField: 'inputGain' }],
  [PREMIX_PARAM, { dimensional: false, stateField: 'premix' }],
]);
/**
 * Two tempo params the write gate answers BY NAME, deliberately not rows in the table: the table
 * describes per-voice PM routing (publish path + snapshot slot + lease scope) and neither is that.
 * `sync-bpm` is the SESSION tempo — one value for the whole room, no voice and no lease; any
 * player holding at least one voice may drive it (spectators may not). `sync-track-bpm` is the
 * audiofile-BPM readout, a mirror of the deck's routed `/voice/<id>/native-tempo` field — the deck
 * decides, so the gate only asks who holds the deck.
 */
const SYNC_BPM_PARAM = 'sync-bpm';
const SYNC_TRACK_BPM_PARAM = 'sync-track-bpm';
// ── projections of the table for the hot-path loops/membership tests — derived, never declared ──
/** The three axes themselves — what a dimension-switch bundle carries. */
const ROUTED_AXES = new Set([...ROUTED_PARAMS].filter(([, p]) => p.axis).map(([name]) => name));
/** The Cosmic LFO knobs — the snapshot apply lands these after the LFO's discrete state. */
const COSMIC_PARAMS = new Set(
  [...ROUTED_PARAMS].filter(([, p]) => p.cosmicKnob).map(([name]) => name),
);
/** Everything routed that carries a DIMENSION: the axes plus the tool params on them. */
const ROUTED_DIMENSIONAL = new Set(
  [...ROUTED_PARAMS].filter(([, p]) => p.dimensional).map(([name]) => name),
);
/** The gain stages in signal order — the table's non-dimensional rows. */
const GAIN_PARAMS = Object.freeze(
  [...ROUTED_PARAMS].filter(([, p]) => !p.dimensional).map(([name]) => name),
);
/** The axes carrying a Cosmic LFO — one instance each, at `ctx.cosmicLFOManager[axis]`. */
const COSMIC_AXES = Object.freeze(['x', 'y', 'z']);
/**
 * The Cosmic LFO's discrete controls: the enable hexagon, the two selects, and the momentary
 * ×0.5 / ×2 kicks. Its two KNOBS are ordinary routed params (see COSMIC_PARAMS) and are not here.
 *
 * Each of these is a single act — press it and it is done — so they take no lease: either player
 * may press again at any moment and the last press wins. The two knobs keep their leases, because
 * a hand really does rest on a knob.
 */
const COSMIC_FIELDS = Object.freeze(['enable', 'source', 'waveform', 'kick', 'rate']);
/**
 * The axes with a device-sensor toggle. Same three, but a separate list on purpose: these are not
 * per-voice objects like the LFOs — there is ONE sensor controller per client, because there is one
 * phone in one hand, and it drives whichever deck that player is on.
 */
const SENSOR_AXES = Object.freeze(['x', 'y', 'z']);
/** Continuous (`live`) publishes per voice+param are thinned to this interval; commits always send. */
const LIVE_PUBLISH_MIN_INTERVAL_MS = 33;
/**
 * Priority for remote writes: top. The manager's simultaneity window would otherwise reject a
 * remote value landing within ~50ms of an equal-priority local gesture (e.g. right after a
 * handover) — and local input on a remotely-owned voice is gate-blocked anyway, so nothing
 * legitimate competes with these writes.
 */
const REMOTE_WRITE_PRIORITY = 0;
/**
 * After minting a room we must be seated (`room:joined`) within this window. If the mint is refused
 * AND the buffered join never seats us, the room does not exist / we can't join — tear down rather
 * than leave the realm locked to a nonexistent room. A creator-refresh (room already exists) seats
 * normally well inside this window, so it never trips.
 */
const CREATE_JOIN_TIMEOUT_MS = 8000;
/**
 * A synced deck load is beat-locked: the deck stays "arming" (not playable) until every granted
 * player has acked the load. This is the barrier's escape hatch — if an ack never arrives (a peer
 * stalled or dropped), release the deck with a plain warning rather than block playback forever.
 */
const DECK_LOAD_BARRIER_MS = 30000;
/**
 * The relay stores only the LAST `player:state` per deck — it never folds live `player:param`
 * events into it. So every routed edit re-publishes this client's own states, trailing-debounced
 * by this much: without the refresh, a rejoiner/late joiner materializes the deck as of the last
 * STRUCTURAL republish (deck load), losing every later dimension switch, knob move, loop, panel…
 */
const STATE_REFRESH_DEBOUNCE_MS = 1000;
/** Fallback delay for the post-transport snapshot refresh when the quantized beat can't be read. */
const TRANSPORT_REFRESH_FALLBACK_MS = 3000;
/** The room shape caps at 4 either way — the same bound the relay enforces at `room:create`. */
const ROOM_SHAPE_MAX = 4;

/**
 * The relay's `{ voices, players }` as counts, or null when it said nothing usable. A relay that
 * omits the shape (an older build) leaves the client on what it booted with rather than being
 * corrected to nonsense — so anything we cannot read is treated as "not stated", not as zero.
 */
function readRelayShape(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const count = (value) =>
    Number.isInteger(value) && value >= 1 && value <= ROOM_SHAPE_MAX ? value : null;
  const voices = count(raw.voices);
  const players = count(raw.players);
  return voices == null && players == null ? null : { voices, players };
}

export class RoomClient {
  #adapter;
  #roomId;
  #clientId;
  #displayName;
  #registry;
  /** The sensor controller CLASS — the seam is held there, not on the instance (see #installSensorBridge). */
  #sensorController;
  /** Listener keeping this client's device in step with the room's switch across dimension changes. */
  #dimensionSensorSync = null;
  #store;
  #now;
  #persistIdentity;
  #stamp;
  #seqTracker = createSeqTracker();
  #teeUnsub = null;
  #registryUnsubs = [];
  #storeUnsub = null;
  #scoreUnsub = null;
  // What became of this session, folded as it happens and filed once on the way
  // out. Owned by the client rather than by the store because it has to
  // outlive the store's own reset in `dispose` — the report is sent from there,
  // and a fold that had already been cleared would have nothing to say.
  #outcome = new SessionOutcome();
  #unloadReport = null;
  // runtime voiceId → { ctx, controller, unsubscribeParams, cover }
  #voices = new Map();
  // Reentrancy latch: while a remote event is being applied, local subscribers stay silent and the
  // write gate lets every consequential write through (they ARE the remote event).
  #applyingRemote = false;
  // Per-voice last dimension we published, so one dimension switch → one bundle (not one per axis).
  #lastPublishedDim = new Map();
  // Per voice+param+dim `live` throttle timestamps.
  #lastLiveSentAt = new Map();
  // Per voice+param+dim GENERATOR throttle timestamps — its own map, so a generator sample can
  // never delay the first sample of a human gesture on the same control (or the other way round).
  #lastGenSentAt = new Map();
  // Serialized last stage→voice map, so only a REAL structural change republishes own states.
  // Late-boot state snapshots: runtime voiceId → last seen player state, applied when the voice
  // (and later its engine) lands.
  #pendingStates = new Map();
  // Wire-keyed snapshots for a deck not yet materialized locally (a late joiner's empty deck): wire
  // id → state, moved to #pendingStates once a runtime voice maps to that wire (setVoiceOrder).
  #pendingWireStates = new Map();
  // The deck-load barrier: wire id → { loadId, ackedBy:Set<clientId>, timer }. A deck is present here
  // while ARMING (loaded locally, waiting for every holder's ack); absent = released = playable.
  #deckLoads = new Map();
  // wire id → when this client last published an edit to it. A stored snapshot is older than a
  // gesture still in flight, so a resync skips a deck this hand is on (see the player:states case).
  #lastLocalEditAt = new Map();
  // wire id → a repair snapshot that arrived while this hand was on that deck. HELD, not dropped:
  // the value we missed is in there, and discarding it would leave us publishing our stale view
  // over it once the gesture settles.
  #deferredStates = new Map();
  // Per-voice promise chain serializing REMOTE transport applies: the relay delivers events in
  // order, and chaining keeps that order through the adapter's async stop/start bodies (a start
  // arriving during an in-flight stop must run after it, or the older stop wins the race).
  #transportQueues = new Map();
  /** voiceId → the launch grid the user had before the room overrode 'none'. Restored on detach. */
  #preRoomLaunchGrid = new Map();
  /**
   * The voices whose Cosmic LFOs this client DRIVES. Every client runs its own LFO instances and
   * they now share every setting, but not a phase — so if all of them pushed values, one deck would
   * modulate along as many different sweeps as there are players. One client per deck writes the
   * axis and the rest take the values off the wire, exactly as they did when only the player who
   * switched the LFO on had one running at all.
   *
   * It is the deck's SPEAKER — the same holder, derived the same way on every client, that already
   * publishes the deck's snapshot.
   *
   * Held as a set so the per-frame question the LFO asks is one lookup, not a re-derivation.
   */
  #cosmicGenerators = new Set();
  /** Voices whose Cosmic LFOs already carry the room bridge (see `#refreshCosmicWiring`). */
  #cosmicBridged = new Set();
  #chat = null;
  #cameraUnsub = null;
  #unbindVoiceLookup = null;
  // Trailing-debounce timer for the own-states refresh that keeps the relay's stored snapshot
  // fresh (see STATE_REFRESH_DEBOUNCE_MS), plus a second one-shot timer armed past the quantized
  // transport boundary — a start/stop publishes BEFORE it fires, so the debounced refresh alone
  // would snapshot `isPlaying` too early and store the pre-transport truth.
  #stateRefreshTimer = null;
  #transportRefreshTimer = null;
  #disposed = false;

  #voiceCount;
  // Mint spec when this tab opened the room (`?create=1`): { players, grantId, rosterUrl } | null.
  // A joiner (share link, no create flag) carries null and only joins.
  #create;
  // Create/join handshake tracking (creator only): true from mint until room:joined seats us. A
  // second relay refusal in that window (create refused AND join refused) fails immediately; a
  // watchdog covers the "no join at all" case.
  #createPending = false;
  /** True once a join has gone out, so a reconnect's rejoin is told apart from the arrival. */
  #joinSent = false;
  #createErrorSeen = false;
  #joinWatchdog = null;

  /**
   * @param {object} deps
   * @param {*} deps.adapter — the room WebSocket sync adapter (raw tee + sendRaw).
   * @param {string} deps.roomId
   * @param {Array<string|null>} deps.voiceOrder — the positional stage→voice map (index i is wire slot
   *   i, the runtime voice on deck i, or null for an empty deck). Republished as decks load/clear.
   * @param {number|null} [deps.voiceCount] — the room SHAPE (deck count) a `create` mint sends as
   *   `shape.voices`. Fixed at creation, NEVER the roster/pool size. Falls back to the mapped voice
   *   count for an inline-roster boot (whose voices ARE its roster).
   * @param {string} [deps.clientId]
   * @param {string} [deps.displayName]
   * @param {{ players: (number|null), grantId: (string|null), rosterUrl: (string|null),
   *   score?: (object|null), draw?: (object|null), pinned?: (Array<{id:string,kind:string}>|null) } | null} [deps.create]
   *   when set, MINT the room (`room:create`) before joining — the creator's tab only. The roster
   *   size (`shape.voices`) comes from `voiceOrder`; a joiner passes null and only joins.
   * @param {*} [deps.registry] — voice registry (injectable for tests).
   * @param {*} [deps.store] — room store (injectable for tests).
   * @param {() => number} [deps.now]
   * @param {(identity: {clientId?: string, reclaimToken?: string}) => void} [deps.persistIdentity]
   *   identity persistence (injectable for tests; defaults to the identity module bound to this
   *   room, which also refreshes the room's localStorage restart copy).
   */
  constructor({
    adapter,
    roomId,
    voiceOrder = [],
    voiceCount = null,
    clientId = getRoomClientId(),
    displayName = resolveDisplayName(),
    create = null,
    registry = voiceRegistry,
    store = roomStore,
    now = () => Date.now(),
    persistIdentity = null,
    sensorController = SensorController,
  }) {
    this.#adapter = adapter;
    this.#roomId = roomId;
    this.#clientId = clientId;
    this.#displayName = displayName;
    // Default persistence binds THIS room, so every adopted ack also refreshes the room's
    // localStorage restart copy (browser-restart reclaim), not just the per-tab sessionStorage.
    this.#persistIdentity = persistIdentity ?? ((identity) => adoptRoomIdentity(identity, roomId));
    // The mint shape's voice count is the fixed DECK count when given; otherwise the mapped voice
    // count (inline-roster boots have no separate deck count — their voices are their roster).
    const mappedCount = Array.isArray(voiceOrder)
      ? voiceOrder.filter((id) => typeof id === 'string' && id.length > 0).length
      : 0;
    this.#voiceCount = Number.isInteger(voiceCount) && voiceCount > 0 ? voiceCount : mappedCount;
    this.#create = create;
    this.#registry = registry;
    this.#sensorController = sensorController;
    this.#store = store;
    // The store reads connectivity from `peers`, which is the relay's view of the OTHERS — it has to
    // be told which one we are, or every "is this holder connected" answer leaves us out.
    this.#store.update({ selfClientId: clientId ?? null });
    // The lease seam keys holds by the positional deck id the addresses carry; a mounted control
    // asks about itself in runtime ids. This client already owns that translation, so it hands the
    // lookup over rather than have the seam keep a second copy of the roster. The returned unbind
    // is scoped to THIS binding, so a late teardown cannot blank a room that has since replaced us.
    this.#unbindVoiceLookup = roomLeases.bindVoiceLookup((voiceId) => this.#store.wireIdForVoice(voiceId));
    this.#now = now;
    this.#stamp = createEnvelopeStamper({
      now,
      getSessionEpoch: () => this.#store.state.sessionEpoch,
    });

    this.#store.setVoiceOrder(voiceOrder);
    this.#store.update({ roomId });

    // Room chat plumbing (no UI yet — the panel design is deferred). Same socket, same identity.
    this.#chat = createChatClient({
      sendRaw: (m) => this.#adapter.sendRaw(m),
      roomId,
      // Live read: a failed seat reclaim switches the effective clientId mid-session, and chat
      // attribution + the self-mute guard must follow the identity we ACTUALLY hold.
      getClientId: () => this.#clientId,
      now,
    });

    // Deck-load seam (two-phase): the collection app deals cards / reports local load completion, we
    // own the wire protocol + the arming barrier. Incoming loads/acks are applied through the bridge.
    setRoomDeckHandlers({
      publish: (stageIndex, entryRef) => this.#publishDeckLoad(stageIndex, entryRef),
      publishClear: (stageIndex) => this.#publishDeckClear(stageIndex),
      notifyLoaded: (stageIndex, loadId) => this.#onLocalDeckLoaded(stageIndex, loadId),
      notifyFailed: (stageIndex, loadId) => this.#onLocalDeckFailed(stageIndex, loadId),
    });

    // Per-deck panel mirror: a holder's panel / info-panel change publishes (owner-gated here);
    // every client — incl. spectators — applies the mirror through the bridge. UI-state, so it lives
    // outside the PM/transport paths.
    setRoomPanelPublishers({
      panel: (voiceId, panelId) => this.#publishPanel(voiceId, panelId),
      info: (voiceId, mode) => this.#publishInfoPanel(voiceId, mode),
      loopSize: (voiceId, beats) => this.#publishLoopSize(voiceId, beats),
      gainChannel: (voiceId, channel) => this.#publishGainChannel(voiceId, channel),
    });

    // Per-deck camera-focus mirror (what the deck's camera orbits: world | moon). The focus store is
    // already the one owner of that choice, so subscribe to it directly: a holder's local flip
    // publishes (owner-gated, silenced during a remote apply by the latch); everyone applies the
    // mirror. The free orbit/zoom drag stays local — only the focus MODE is shared state.
    this.#cameraUnsub = subscribeCameraFocus((voiceId) => this.#onLocalCameraFocus(voiceId));

    this.#teeUnsub = adapter.onRawMessage((msg) => this.#onMessage(msg));
    // Track voices as they register/unregister (multi boots are staged, so most voices land later).
    this.#registryUnsubs.push(registry.onVoicesChange(() => this.#syncVoices()));
    this.#registryUnsubs.push(registry.onAudioEngineAssigned((ctx) => this.#onEngineAssigned(ctx)));
    this.#storeUnsub = this.#store.subscribe(() => {
      this.#refreshVoiceCovers();
      // Grants and connections decide which client drives each deck's LFOs; both live in the store.
      this.#refreshCosmicWiring();
      this.#outcome.observe(this.#store.state);
    });
    this.#scoreUnsub = roomScore.subscribe(() => {
      // The score reaching its end is what "completed the session" MEANS, and it
      // is watched here rather than asked for at send time because by then the
      // transport has been reset and the answer is gone.
      if (roomScore.current()?.ended) this.#outcome.noteReachedEnd();
      this.#store.refresh();
    });
    // The exits that matter most are the ones nobody clicks. A tab closed
    // mid-session is the commonest early exit there is, and `pagehide` is the
    // last moment anything can be sent — `beforeunload` does not fire reliably on
    // mobile, and `unload` is ignored outright by browsers that have already
    // moved the page into the back/forward cache.
    if (typeof window !== 'undefined') {
      this.#unloadReport = () => this.#outcome.report();
      window.addEventListener('pagehide', this.#unloadReport);
    }
    this.#syncVoices();
    this.#installSensorBridge();
    // ONE subscription for the room, not one per mounted toggle: the controller re-reads its own
    // per-dimension enables whenever the dimension moves and acts on them by itself, so the room's
    // switch — the one everybody can see — has to get the last word.
    if (typeof document !== 'undefined') {
      this.#dimensionSensorSync = () => syncDeviceToSwitch(this.#registry.activeId ?? null);
      document.addEventListener('orbiters:dimension-changed', this.#dimensionSensorSync);
    }
    // …and on FOCUS, the other event the controller re-reads its own stored enables on. Without it,
    // a peer's switch-off on a deck you are not looking at never reaches your controller, and
    // coming back to that deck restores the axis and starts your phone under a toggle reading off.
    if (typeof registry.onActiveChange === 'function') {
      this.#registryUnsubs.push(registry.onActiveChange(
        () => syncDeviceToSwitch(this.#registry.activeId ?? null),
      ));
    }

    // Join now: connect() is idempotent, and both room:create and room:join are buffered until the
    // register handshake completes (flushed in order). Presence in the room does not enable sync —
    // the pulse joins when the room starts. The creator MINTS before joining; a refresh re-mints
    // (the room already exists → the relay refuses the duplicate, harmless) and simply re-joins.
    adapter.connect();
    if (this.#create) this.#sendCreate();
    this.#sendJoin();
  }

  get clientId() {
    return this.#clientId;
  }

  get displayName() {
    return this.#displayName;
  }

  /** The room's chat store/sender (see chatClient.js). UI surfaces read messages from here. */
  get chat() {
    return this.#chat;
  }

  /**
   * Republish the live positional stage→voice map (index i = wire slot i). The collection app calls
   * this every time a deck loads or clears: re-seed the store's wire↔voice translation, attach any
   * voice a prior `#syncVoices` skipped because it wasn't mapped yet, and drain any snapshot that was
   * waiting on that wire (a late joiner's deck that just materialized). A CHANGED map while playing
   * also republishes this client's own states — the relay keeps only the LAST stored state per deck,
   * so a load or a clear must refresh it or a late joiner materializes yesterday's deck.
   */
  setVoiceOrder(order) {
    // The store owns the mapping, so it is also the one that knows whether this re-seeding
    // actually moved anything — asking it beats keeping a second copy of the same order here.
    const changed = this.#store.setVoiceOrder(order);
    // Holds are keyed by DECK, so a deck that just materialized may already be held — and the
    // control that mounted while it was still loading has been drawing itself as free the whole
    // time. Nothing in the ledger changed, so only this can say the answer did.
    if (changed) roomLeases.voiceLookupChanged();
    this.#syncVoices();
    this.#drainPendingWireStates();
    this.#refreshVoiceCovers();
    if (changed && this.#store.state.phase === 'playing') this.publishOwnStates();
  }

  /** Does the local client own deck `stageIndex` right now (holder, playing)? Gates a deck load. */
  ownsDeck(stageIndex) {
    return this.#store.ownsWire(stageIndex, this.#clientId);
  }

  /** Is deck `stageIndex` past its load barrier (playable)? True when it isn't arming. */
  deckReady(stageIndex) {
    return !this.#deckLoads.has(String(stageIndex));
  }

  /** Creator's Start — assigns the grants and moves the room to `playing` (relay-enforced). */
  startRoom() {
    if (this.#store.state.role !== 'creator' || this.#store.state.phase !== 'gathering') return false;
    this.#adapter.sendRaw({ type: 'room:start', roomId: this.#roomId });
    return true;
  }

  /**
   * Say whether this player is ready to begin. The room starts itself once everyone
   * seated has said yes — this client never decides that, and never sets its own flag either: the
   * relay writes it and echoes it back, which is what keeps two players from holding different
   * pictures of who is ready.
   */
  setReady(ready) {
    const { role, phase } = this.#store.state;
    if (phase !== 'gathering') return false;
    if (role !== 'creator' && role !== 'player') return false;
    this.#adapter.sendRaw({ type: 'room:ready', roomId: this.#roomId, ready: ready !== false });
    return true;
  }

  /**
   * Leave for good, now.
   *
   * Going quiet is not the same statement: a closed socket is indistinguishable from a phone in a
   * tunnel, so the relay holds the seat through a grace window (15s gathering, 60s playing) in case
   * that is what happened. Cancel and "Leave collection" are decisions, so they say so — the seat
   * frees at once, the person left behind stops being told someone is still here, and a creator who
   * leaves and comes back does not collide with their own held seat.
   *
   * Fire-and-forget on purpose: this is called on the way out, including from an unload path, and
   * there is nothing useful to do with a failure by the time the page is gone.
   */
  leaveRoom() {
    if (!this.#roomId) return false;
    try {
      this.#adapter.sendRaw({ type: 'room:leave', roomId: this.#roomId });
    } catch {
      // The socket is already gone, which achieves the same thing a beat later via the grace window.
      return false;
    }
    return true;
  }

  /** Any seated player may order a score transport nudge; the relay stamps and folds it. */
  nudgeScore(action) {
    if (!roomScore.active || this.#store.state.phase !== 'playing') return false;
    if (this.#store.state.role !== 'creator' && this.#store.state.role !== 'player') return false;
    this.#adapter.sendRaw({ type: 'room:score-nudge', roomId: this.#roomId, action });
    return true;
  }

  dispose() {
    if (this.#disposed) return;
    this.#disposed = true;
    // FIRST, before anything is torn down. The report is folded from the store
    // and the score, and both are reset further down this method — reporting
    // after that would file an empty session for everyone who left properly.
    // Idempotent, so an unload that already fired costs nothing here.
    this.#outcome.report();
    if (this.#unloadReport && typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.#unloadReport);
      this.#unloadReport = null;
    }
    this.#createPending = false;
    if (this.#joinWatchdog !== null) {
      clearTimeout(this.#joinWatchdog);
      this.#joinWatchdog = null;
    }
    setRoomDeckHandlers({});
    setRoomPanelPublishers({}); // stop publishing local panel changes once the room is gone
    // The sensor switches belonged to that room. Deliberately NOT published off on the way out:
    // whatever is switched on is still switched on for the players who are still there, and one
    // person closing a tab is a poor way for them to find out otherwise.
    if (this.#dimensionSensorSync && typeof document !== 'undefined') {
      document.removeEventListener('orbiters:dimension-changed', this.#dimensionSensorSync);
      this.#dimensionSensorSync = null;
    }
    setRoomSensorPublishers();
    if (this.#stateRefreshTimer !== null) {
      clearTimeout(this.#stateRefreshTimer);
      this.#stateRefreshTimer = null;
    }
    if (this.#transportRefreshTimer !== null) {
      clearTimeout(this.#transportRefreshTimer);
      this.#transportRefreshTimer = null;
    }
    try { this.#cameraUnsub?.(); } catch { /* already gone */ }
    this.#cameraUnsub = null;
    for (const rec of this.#deckLoads.values()) { if (rec.timer) clearTimeout(rec.timer); }
    this.#deckLoads.clear();
    try { this.#teeUnsub?.(); } catch { /* already gone */ }
    this.#registryUnsubs.forEach((u) => { try { u(); } catch { /* noop */ } });
    this.#registryUnsubs = [];
    try { this.#storeUnsub?.(); } catch { /* noop */ }
    try { this.#scoreUnsub?.(); } catch { /* noop */ }
    for (const voiceId of [...this.#voices.keys()]) this.#detachVoice(voiceId);
    this.#chat?.dispose();
    this.#store.reset();
    // Holds belonged to THAT room. Left behind they would lock controls in a realm that is solo
    // again, with no stream left to expire them.
    roomLeases.reset();
    roomScore.reset();
    // So did whether the chat was open and what was still unread in it. The rail is mounted once
    // per tab and follows a replaced room without remounting, so without this the next room would
    // inherit the last one's closed panel — and while a room is gathering there is no bubble to
    // reopen it with.
    resetChatPanel();
    try { this.#unbindVoiceLookup?.(); } catch { /* already gone */ }
    this.#unbindVoiceLookup = null;
    // A client that tears ITSELF down (connection lost, failed handshake) must also leave the module
    // registry — otherwise every deck deal/eject keeps routing through this dead client and silently
    // refuses, which is exactly the room-locked realm the teardown exists to avoid.
    if (activeClient?.client === this) activeClient = null;
  }

  // ── outbound ──────────────────────────────────────────────────────────────

  /**
   * Mint the room before joining (creator's tab only). Shape: the roster size fixes `voices`; the
   * URL's `players` fixes the seat count. Private in v1 (share-link rooms); the grant + canonical
   * share URL (current URL minus the create flag) travel with the record.
   */
  #sendCreate() {
    const { players, grantId, rosterUrl, score, draw, pinned } = this.#create;
    this.#createPending = true;
    this.#adapter.sendRaw({
      type: 'room:create',
      roomId: this.#roomId,
      shape: { voices: this.#voiceCount, players: players ?? null },
      visibility: 'private',
      grantId: grantId ?? null,
      rosterUrl: rosterUrl ?? null,
      score: score ?? null,
      // How this room deals its cards, and the pinned pool it deals them from. There is no
      // `GET /rooms/:grantId`, so a joiner cannot look either of them up — they travel over the
      // relay exactly as the score does, stated here once and replayed in every room:joined.
      draw: draw ?? null,
      pinned: normalizeDrawPinned(pinned),
    });
    // Seat-or-fail: if room:joined never arrives, we minted into a room we can't join.
    if (typeof setTimeout === 'function') {
      this.#joinWatchdog = setTimeout(() => {
        this.#joinWatchdog = null;
        if (this.#disposed || !this.#createPending) return;
        console.warn('[room] room create/join did not complete — leaving room mode');
        this.#failAndTeardown('create-failed');
      }, CREATE_JOIN_TIMEOUT_MS);
    }
  }

  /** Seating succeeded (room:joined): the mint handshake is resolved — stop watching for failure. */
  #resolveCreateHandshake() {
    this.#createPending = false;
    this.#createErrorSeen = false;
    if (this.#joinWatchdog !== null) {
      clearTimeout(this.#joinWatchdog);
      this.#joinWatchdog = null;
    }
  }

  /** Release the room to idle and surface a plain failure the UI can read (isActive → false). */
  #failAndTeardown(reason) {
    this.dispose();
    try { this.#store.update({ error: reason }); } catch { /* store gone */ }
  }

  /**
   * Join, saying whether a SEAT is the point.
   *
   * Joining a full room otherwise succeeds and hands back `role: 'spectator'`,
   * leaving the client to infer "full" from a role string. That is right for
   * someone who followed an invite link and wants to listen, and wrong for
   * someone matchmaking SENT here to play: if the seat went while they were on
   * their way, they have to know, so the page can walk to the next candidate
   * instead of stranding them as a silent listener in a room they never chose.
   *
   * The difference is intent, and intent is on the URL: quick play boots with
   * `?match=1`. Everything else joins exactly as it always did.
   *
   * ONLY on the first join. This is re-sent on every auto-reconnect, and a drop
   * longer than the relay's grace window releases the seat — so a rejoin asking
   * for one could be refused for a room this client was PLAYING in a moment ago,
   * and the refusal would arrive with nothing left to walk back to. The question
   * "was a seat waiting for me when I arrived?" is only ever asked on arrival;
   * afterwards a rejoin takes whatever the room still has, which is exactly what
   * every other rejoin has always done.
   */
  #sendJoin() {
    const requireSeat = isMatchmakingArrival() && !this.#joinSent;
    this.#joinSent = true;
    this.#adapter.sendRaw({
      type: 'room:join',
      roomId: this.#roomId,
      ...(requireSeat ? { requireSeat: true } : {})
    });
  }

  #sendPlayerEvent(fields) {
    this.#adapter.sendRaw(this.#stamp({ roomId: this.#roomId, msgId: randomMsgId(), ...fields }));
    // Every routed EDIT also refreshes the relay's stored snapshot (debounced): the relay keeps
    // only the last full state per deck, so without this a rejoiner materializes stale decks.
    // `player:state` itself must not reschedule (that IS the refresh).
    if (fields.type === 'player:param') {
      // A generator stream never rests, so it may only ARM the snapshot refresh, never push one
      // back: re-armed on every sample, the trailing debounce would simply never fire while an
      // oscillator ran — the stored snapshot going stale for every late joiner, and the deferred
      // repairs the refresh drains held off the deck for as long as it played. Armed-if-idle, the
      // snapshot follows a running generator at most one debounce behind. It is nobody's hand
      // either, so it must not mark the deck hand-edited: that guard exists to keep a resync from
      // rolling a gesture backwards, and a generator's value is refreshed by its own next sample.
      if (fields.generator === true) {
        if (this.#stateRefreshTimer === null) this.#scheduleStateRefresh();
        return;
      }
      // Remember which decks we just touched: a resync must not roll a hand-in-flight backwards.
      // A BUNDLE carries its addresses in `messages` and has no top-level `address` — reading only
      // that would leave every bundled edit (a dimension switch and its axis values) unprotected.
      const addresses = Array.isArray(fields.messages)
        ? fields.messages.map((entry) => entry?.address)
        : [fields.address];
      for (const address of addresses) {
        const parsed = parseAddress(address);
        if (parsed?.wireVoiceId != null) this.#lastLocalEditAt.set(String(parsed.wireVoiceId), this.#now());
      }
      this.#scheduleStateRefresh();
    }
  }

  /** Arm (trailing) the debounced own-states refresh that keeps the relay's snapshot fresh. */
  #scheduleStateRefresh(delayMs = STATE_REFRESH_DEBOUNCE_MS) {
    if (this.#disposed || typeof setTimeout !== 'function') return;
    if (this.#stateRefreshTimer !== null) clearTimeout(this.#stateRefreshTimer);
    this.#stateRefreshTimer = setTimeout(() => {
      this.#stateRefreshTimer = null;
      if (this.#disposed || this.#store.state.phase !== 'playing') return;
      // Take any held repair FIRST. Publishing before applying it would push our stale view of that
      // deck out to everyone — the missed value would be lost for the room, not just for us.
      const drained = this.#drainDeferredStates();
      this.publishOwnStates({ skipWires: drained });
    }, delayMs);
  }

  /**
   * Arm the post-transport snapshot refresh PAST the quantized boundary: a start publishes before
   * the shared beat it schedules for, so a refresh inside the count-in would store `isPlaying`
   * as it was BEFORE the transport event — exactly the stale truth a late joiner must not get.
   */
  #scheduleTransportStateRefresh(atBeat) {
    if (this.#disposed || typeof setTimeout !== 'function') return;
    let delayMs = TRANSPORT_REFRESH_FALLBACK_MS;
    const toBeat = delayMsToBeat({
      beatNow: syncCoordinator.getCurrentBeat?.(),
      targetBeat: atBeat,
      bpm: syncCoordinator.bpm,
    });
    if (Number.isFinite(toBeat) && toBeat >= 0) delayMs = toBeat + 500;
    if (this.#transportRefreshTimer !== null) clearTimeout(this.#transportRefreshTimer);
    this.#transportRefreshTimer = setTimeout(() => {
      this.#transportRefreshTimer = null;
      if (this.#disposed || this.#store.state.phase !== 'playing') return;
      this.publishOwnStates();
    }, delayMs);
  }

  /**
   * `intent` is the whole lease signal: `live` says a hand is on this control and `commit` says it
   * let go. Both were already stamped on every local change and read no further than the publish
   * throttle — putting them on the envelope is what lets every client fold the same gesture
   * boundaries out of the stream. A field, not a message: an announcement separate from the write
   * could arrive on the other side of it and describe a gesture that had already moved on.
   */
  #publishParam(voiceId, axis, dimensionId, normalized, intent, { generated = false } = {}) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null || !Number.isFinite(normalized)) return;
    this.#sendPlayerEvent({
      type: 'player:param',
      address: paramAddress(wireId, axis, dimensionId),
      args: [normalized],
      intent,
      // On the envelope, like `intent`, because every receiver's ledger needs it: a generator's
      // write is nobody's gesture, so folding it as one would close the sender's own open hold
      // on the same control (see #foldLease).
      ...(generated ? { generator: true } : {}),
    });
  }

  /** Publish one gain stage on its dedicated single-param path (`/param/<gain-param>/main`).
   *  Carries the generator marker for the same reason #publishParam does — the fold must never
   *  read a generator's stream as a gesture, whichever routed param it happens to drive. */
  #publishGain(voiceId, paramName, normalized, intent, { generated = false } = {}) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null || !Number.isFinite(normalized)) return;
    this.#sendPlayerEvent({
      type: 'player:param',
      address: paramAddress(wireId, paramName, NO_DIMENSION),
      args: [normalized],
      intent,
      ...(generated ? { generator: true } : {}),
    });
  }

  /** One atomic bundle for a dimension switch: the new dimension + its axis values. */
  #publishDimensionBundle(voiceId, dimensionId, pm) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null) return;
    const messages = [{ address: dimensionAddress(wireId), args: [dimensionId] }];
    for (const axis of ROUTED_AXES) {
      const normalized = pm.getNormalizedValue(axis, dimensionId === NO_DIMENSION ? null : dimensionId);
      if (Number.isFinite(normalized)) {
        messages.push({ address: paramAddress(wireId, axis, dimensionId), args: [normalized] });
      }
    }
    this.#sendPlayerEvent({ type: 'player:param', bundle: true, messages });
  }

  /**
   * Put a structural deck load COMMAND on the wire (rides `player:param` like transport — the relay
   * owner-gates it on the `/voice/<id>/` prefix, so only the deck's owner can deal). The wire id is the
   * deck/stage index directly (positional), so an EMPTY deck with no runtime voice still addresses
   * correctly. The entry ref's id field names the card kind: a track deal or an ORBITER swap rebuilds
   * the deck's audio engine, so both mint + arm the two-phase load barrier (returns the loadId so the
   * collection app can ack when its own local load finishes). A WORLD swap is visuals only — the
   * engine keeps playing — so it ships single-shot (no loadId, no barrier, best-effort everywhere).
   */
  #publishDeckLoad(stageIndex, entryRef) {
    if (!entryRef || typeof entryRef !== 'object') return null;
    const barrier = !entryRef.entangledWorldId;
    const loadId = barrier ? randomMsgId() : null;
    if (loadId) this.#armDeck(stageIndex, loadId);
    this.#sendPlayerEvent({
      type: 'player:param',
      address: loadAddress(String(stageIndex)),
      args: [loadId ? { ...entryRef, loadId } : { ...entryRef }],
    });
    return loadId;
  }

  /**
   * Put a deck CLEAR on the wire (`/voice/<id>/load [null]` — "load nothing", the same null-clear
   * convention the loop range uses). No barrier: an empty deck has nothing to arm, and a clear must
   * never wait on peers to take effect. It also releases any in-flight load barrier on that deck —
   * the load it was arming no longer has anything to become playable.
   */
  #publishDeckClear(stageIndex) {
    this.#releaseDeck(stageIndex, 'cleared');
    this.#sendPlayerEvent({
      type: 'player:param',
      address: loadAddress(String(stageIndex)),
      args: [null],
    });
  }

  /** The local load of `loadId` on this deck finished — record our own ack and tell the room. */
  #onLocalDeckLoaded(stageIndex, loadId) {
    if (!loadId) return;
    this.#recordDeckAck(stageIndex, loadId, this.#clientId);
    // A settled structural change on a deck WE own refreshes the relay's stored snapshot (an
    // orbiter swap keeps the voice map unchanged, so the map-change republish never fires for it).
    if (this.#store.ownsWire(stageIndex, this.#clientId)) this.publishOwnStates();
    // `load-ready` is about the SENDER's readiness, so any participant (incl. a non-owner / spectator)
    // must be able to publish it — it can't ride the owner-gated `/voice/<id>/` address. Distinct type,
    // relayed ungated by the server (same class as player:state-request); see the wire-contract note.
    this.#adapter.sendRaw({
      type: 'player:load-ready', roomId: this.#roomId, voiceId: String(stageIndex), loadId, clientId: this.#clientId,
    });
  }

  /** The local load of `loadId` failed — surface it to the room and release the local barrier. */
  #onLocalDeckFailed(stageIndex, loadId) {
    if (!loadId) return;
    this.#adapter.sendRaw({
      type: 'player:load-failed', roomId: this.#roomId, voiceId: String(stageIndex), loadId, clientId: this.#clientId,
    });
    this.#releaseDeck(stageIndex, 'failed', loadId);
  }

  /** Arm the barrier for a deck load: it becomes non-playable until every holder acks this loadId. */
  #armDeck(stageIndex, loadId) {
    const key = String(stageIndex);
    const prev = this.#deckLoads.get(key);
    if (prev?.timer) clearTimeout(prev.timer);
    const timer = typeof setTimeout === 'function'
      ? setTimeout(() => this.#releaseDeck(stageIndex, 'timeout'), DECK_LOAD_BARRIER_MS)
      : null;
    // Grant holders (the players) must all ack; spectators load best-effort, outside the barrier.
    // SNAPSHOT them here: the barrier waits on exactly the holders the load command reached. Re-reading
    // the live grant map on every ack makes it unsatisfiable the moment a handover seats someone new —
    // they can't ack a loadId they were never sent, so the deck would hang until the timeout.
    // CONNECTED holders only. A seat survives the disconnect grace window, so a holder who closed
    // their tab is still in the grant map — and a barrier that waits on them holds the deck
    // unplayable for the full timeout for an ack that can never arrive.
    const connected = this.#store.connectedClientIds();
    const holders = new Set([...grantHolders(this.#store.state.grants)].filter((id) => connected.has(id)));
    this.#deckLoads.set(key, { loadId, ackedBy: new Set(), holders, timer });
    this.#publishArmingState();
  }

  /** Record `clientId`'s readiness ack for a deck load; release the barrier once all holders acked. */
  #recordDeckAck(stageIndex, loadId, clientId) {
    const rec = this.#deckLoads.get(String(stageIndex));
    if (!rec || rec.loadId !== loadId || !clientId) return;
    rec.ackedBy.add(clientId);
    this.#releaseIfAcked(stageIndex, rec);
  }

  /** Release a deck's barrier once every holder it is waiting on has acked. */
  #releaseIfAcked(stageIndex, rec) {
    if (rec.holders.size > 0 && [...rec.holders].every((h) => rec.ackedBy.has(h))) {
      this.#releaseDeck(stageIndex, 'ready');
    }
  }

  /** A participant left — stop waiting on their ack, or an in-flight barrier hangs until it times out. */
  #dropDeckHolder(clientId) {
    if (!clientId) return;
    for (const [key, rec] of this.#deckLoads) {
      if (rec.holders.delete(clientId)) this.#releaseIfAcked(key, rec);
    }
  }

  /**
   * Release a deck's load barrier (all acked / a failure / the timeout) — the deck becomes playable.
   *
   * `loadId`, when given, is the load the verdict came from and MUST still be the one armed here. A
   * deck can be re-armed while an earlier load is still settling — a re-deal, or a card swapped onto
   * it — and that earlier load's late failure must not release a barrier now standing for a
   * different card, or everyone starts playing a deck that is still arming. Verdicts that carry no
   * load id (a clear, the barrier's own timeout) are about the deck itself and always apply.
   */
  #releaseDeck(stageIndex, reason, loadId = null) {
    const key = String(stageIndex);
    const rec = this.#deckLoads.get(key);
    if (!rec) return;
    if (loadId && rec.loadId !== loadId) return; // a superseded load's verdict — not this barrier's

    if (rec.timer) clearTimeout(rec.timer);
    if (reason === 'timeout') console.warn('[room] deck load barrier timed out — releasing deck', key);
    else if (reason === 'failed') console.warn('[room] deck load failed — releasing deck', key);
    this.#deckLoads.delete(key);
    this.#publishArmingState();
  }

  /** Publish the set of arming (loading, not-yet-playable) decks to the store for UI to reflect. */
  #publishArmingState() {
    this.#store.update({ armingDecks: [...this.#deckLoads.keys()] });
  }

  #publishTransport(voiceId, kind, atBeat) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null) return;
    this.#sendPlayerEvent({
      type: 'player:param',
      address: transportAddress(wireId, kind),
      args: [Number.isFinite(atBeat) ? atBeat : null],
    });
    // The playback flag flips only AT the quantized beat — refresh the stored snapshot after it.
    this.#scheduleTransportStateRefresh(atBeat);
  }

  /** Publish a SEEK for an owned voice as the phase ANCHOR it establishes (see `seekAddress`). */
  #publishSeek(voiceId, anchorBeat, anchorSourceMs) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null) return;
    this.#sendPlayerEvent({
      type: 'player:param',
      address: seekAddress(wireId),
      args: [anchorBeat, anchorSourceMs],
    });
    // Same reason a transport start refreshes past its beat: the anchor is only INSTALLED at the
    // future bar, so the debounced refresh that `#sendPlayerEvent` just armed would store the
    // PRE-seek phase — and the relay keeps only the last snapshot, so a late joiner would come in
    // on the old phase indefinitely.
    this.#scheduleTransportStateRefresh(anchorBeat);
  }

  /** Publish a loop RANGE (or a clear, when startMs is null) for an owned voice. */
  #publishLoopRange(voiceId, startMs, endMs, active) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null) return;
    const args = Number.isFinite(startMs) && Number.isFinite(endMs)
      ? [startMs, endMs, active ? 1 : 0]
      : [null]; // a clear
    this.#sendPlayerEvent({ type: 'player:param', address: loopRangeAddress(wireId), args });
  }

  /** Publish a loop ENABLE/disable toggle for an owned voice. */
  #publishLoopEnable(voiceId, active) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null) return;
    this.#sendPlayerEvent({ type: 'player:param', address: loopEnableAddress(wireId), args: [active ? 1 : 0] });
  }

  /**
   * Publish the loop SUBDIVISION selection for an owned voice (`null` = no preset). A view mirror on
   * top of the already-routed range: the range is what the two clients HEAR, this is what their two
   * panels READ. Owner + phase gated here, exactly like the panel mirror.
   */
  #publishLoopSize(voiceId, beats) {
    if (!this.#canPublishVoiceUi(voiceId)) return;
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null) return;
    this.#sendPlayerEvent({
      type: 'player:param',
      address: loopSizeAddress(wireId),
      args: [Number.isFinite(beats) ? beats : null],
    });
  }

  /**
   * Publish which gain stage this voice's rail is pointed at (`'input' | 'output'`). A view mirror:
   * both gains are routed and stay bound either way, so this shares only what is on SHOW — but a deck
   * everyone watches should show everyone the same stage, or two players read different numbers off
   * what looks like the same fader.
   */
  #publishGainChannel(voiceId, channel) {
    // Says whether the flip was TAKEN, not just fire-and-forget like the other UI mirrors: the
    // switch is one shared view, so a press this client may not publish must be refused back at
    // the rail. Swallowed instead, the rail flips locally, shows a stage nobody else sees, and the
    // holder's next snapshot snaps it back — a switch that visibly "doesn't work" rather than one
    // that is not yours.
    //
    // Inlined rather than routed through #canPublishVoiceUi (other publishers share that gate as a
    // single yes/no) because the two failure modes it folds together need different answers here:
    // the deck still MATERIALIZING (room not yet 'playing', or this voice has no wireId yet) is not
    // a refusal — the rail may flip locally, the bridge remembers the value, and the opening
    // player:state snapshot sent at Start carries `gainChannel` (see #voiceStateSnapshot), so the
    // flip still reaches the room once the wire exists. Only "this voice isn't yours to edit" is an
    // actual refusal.
    if (this.#store.state.phase !== 'playing') return true; // not on the wire yet — rides the snapshot
    if (!this.#store.canEditVoice(voiceId, this.#clientId)) return false; // not yours
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null) return true; // deck still materializing — rides the snapshot once it has a wireId
    this.#sendPlayerEvent({
      type: 'player:param',
      address: gainChannelAddress(wireId),
      args: [channel],
    });
    return true;
  }

  /** Publish this voice's NATIVE (audiofile) tempo — a projection input, so the room must agree. */
  #publishNativeTempo(voiceId, bpm) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null || !Number.isFinite(bpm)) return;
    this.#sendPlayerEvent({ type: 'player:param', address: nativeTempoAddress(wireId), args: [bpm] });
  }

  /** Publish this voice's DOWNBEAT (grid marker, seconds) — the other projection input. */
  #publishDownbeat(voiceId, sec) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null || !Number.isFinite(sec)) return;
    this.#sendPlayerEvent({ type: 'player:param', address: downbeatAddress(wireId), args: [sec] });
  }

  /** Publish this voice's LAUNCH GRID in bars — the boundary shared events quantize to. */
  #publishLaunchGrid(voiceId, bars) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null || !Number.isFinite(bars)) return;
    this.#sendPlayerEvent({ type: 'player:param', address: launchGridAddress(wireId), args: [bars] });
  }

  /** Publish a WARP on/off toggle for an owned voice (its own single-param path, like loop enable). */
  #publishWarp(voiceId, on) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null) return;
    this.#sendPlayerEvent({ type: 'player:param', address: warpAddress(wireId), args: [on ? 1 : 0] });
  }

  /** Publish a SYNC on/off toggle for an owned voice (its own single-param path, like warp). */
  #publishSync(voiceId, on) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null) return;
    this.#sendPlayerEvent({ type: 'player:param', address: syncAddress(wireId), args: [on ? 1 : 0] });
  }

  /**
   * Publish one Cosmic LFO act on one axis of an owned voice — the enable, either select, or a
   * ×0.5/×2 kick. All four take the same single-value path a warp toggle does: no quantum (nothing
   * is scheduled), no lease (nobody's hand is on a button), last press wins.
   */
  #publishCosmic(voiceId, axis, field, value) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null) return;
    this.#sendPlayerEvent({ type: 'player:param', address: cosmicAddress(wireId, axis, field), args: [value] });
  }

  /**
   * Publish the device-SENSOR enable on one axis of an owned voice. Same single-value path as the
   * warp toggle: no quantum, no lease, last press wins — and here the last press also settles whose
   * phone is on the axis, because switching it on is a claim with your own device.
   */
  #publishSensor(voiceId, axis, on) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null) return;
    this.#sendPlayerEvent({ type: 'player:param', address: sensorAddress(wireId, axis), args: [on ? 1 : 0] });
  }

  /** Publish a per-deck interaction-PANEL selection for an owned voice (view mirror; owner-gated here). */
  #publishPanel(voiceId, panelId) {
    if (!this.#canPublishVoiceUi(voiceId) || typeof panelId !== 'string' || panelId.length === 0) return;
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null) return;
    this.#sendPlayerEvent({ type: 'player:param', address: panelAddress(wireId), args: [panelId] });
  }

  /** Publish a per-deck INFO-panel open/close for an owned voice (`null` = closed). */
  #publishInfoPanel(voiceId, mode) {
    if (!this.#canPublishVoiceUi(voiceId)) return;
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null) return;
    this.#sendPlayerEvent({ type: 'player:param', address: infoPanelAddress(wireId), args: [mode ?? null] });
  }

  /** Owner + phase gate for a voice-UI publish (panel/camera mirror) — same rule as an edit publish. */
  #canPublishVoiceUi(voiceId) {
    return this.#store.state.phase === 'playing' && this.#store.canEditVoice(voiceId, this.#clientId);
  }

  /** A local camera-focus flip — publish the mirror for an owned voice (remote applies are latched). */
  #onLocalCameraFocus(voiceId) {
    if (this.#applyingRemote || this.#disposed) return;
    if (!this.#canPublishVoiceUi(voiceId)) return;
    const wireId = this.#store.wireIdForVoice(voiceId);
    if (wireId === null) return;
    this.#sendPlayerEvent({
      type: 'player:param',
      address: cameraAddress(wireId),
      args: [getCameraFocus(voiceId)],
    });
  }

  /**
   * Publish the full state of this client's voices — the snapshot a late joiner materializes from,
   * and the answer to a gap.
   *
   * `openingOnly` narrows it to the voices this client SPEAKS for, and exists for exactly one
   * moment: the room opening (Start, and the republish after a handover). There, several clients
   * hold the same voice and their pictures legitimately DIFFER — the projection inputs resolve per
   * user — so if all of them published, the relay's single stored copy would be whichever landed
   * last and the clients would apply each other's.
   *
   * Every other call publishes every voice this client holds, because by then the room has
   * converged and the client that just touched a voice has the freshest view of it. Narrowing those
   * would leave a voice edited by a non-speaker with a stored snapshot nobody ever refreshes.
   */
  publishOwnStates({ openingOnly = false, skipWires = null } = {}) {
    const contents = deckContents();
    // The hand's played cards, read once for the whole publish: it is a property of the ROOM's deal,
    // not of any one deck, and every snapshot carries the same copy so a repair can take the union
    // of whichever ones it happens to receive.
    const hand = handConsumed();
    const answered = new Set();
    for (const voiceId of this.#store.ownedVoiceIds(this.#clientId)) {
      const wireId = this.#store.wireIdForVoice(voiceId);
      if (wireId === null) continue;
      if (openingOnly && !this.#store.speaksForWire(wireId, this.#clientId)) continue;
      // A deck we just took the relay's own snapshot for: the stored copy IS what we applied, so
      // republishing adds nothing — and parts of applying it (a deck clear) settle asynchronously,
      // so a snapshot built right now could still describe the card that snapshot removed.
      if (skipWires?.has(String(wireId))) continue;
      const ctx = this.#registry.get(voiceId);
      if (!ctx?.parameterManager) continue;
      // A deck still ADOPTING the room's picture of itself must not publish its own. A freshly
      // built deck always comes up STOPPED — that is deliberate, you press play — and the snapshot
      // saying the room is playing cannot land until the deck's engine does. Publishing in that gap
      // states `isPlaying: false` as this deck's truth, and every peer stops the deck they are
      // watching. It never comes back either: the start that eventually lands here is a REMOTE
      // apply, and those deliberately do not echo out, so nothing ever tells them otherwise. That
      // is a reloading player silencing a deck on everyone else's screen for the rest of the
      // session. Bounded by the engine — the moment it lands the snapshot applies and clears itself.
      if (this.#pendingStates.has(voiceId) && !ctx.audioEngine) continue;
      const state = this.#buildVoiceState(ctx);
      // Carry the deck's loaded card so a late joiner materializes the deck (not just its params).
      const deck = contents[Number(wireId)] ?? null;
      if (deck) state.deck = deck;
      if (hand) state.hand = hand;
      this.#sendPlayerEvent({ type: 'player:state', voiceId: wireId, state });
      answered.add(String(wireId));
    }
    // Owned decks with NO runtime voice (never dealt, or CLEARED) publish an explicitly EMPTY state
    // (no `deck` field) — the relay keeps the LAST stored state per deck, so without this a late
    // joiner would materialize a card the owner ejected long ago. A mapped-but-still-booting voice is
    // deliberately skipped (its load is in flight; the settled state follows) — and so, for the same
    // reason, is a deck whose card has not reached a voice YET. Both are "loading", which a snapshot
    // cannot say: an empty one reads as an eject, and every peer would empty a deck that is filling.
    // That is the whole of a REJOIN — this client owns decks the room says hold cards, and it has
    // not built any of them yet — so publishing here cut the other player's decks out and made them
    // reload when the settled state arrived.
    // Read the CURRENT grants, not the last stored snapshot: under a score the section that owns a
    // deck can change on a deadline with no wire message, and a voice granted to nobody in the
    // previous section is absent from that snapshot entirely — so iterating it would skip a deck
    // this client owns right now, which is exactly what the `ownsWire` test below already asks.
    for (const wireId of Object.keys(this.#store.currentGrants() ?? {})) {
      if (!this.#store.ownsWire(wireId, this.#clientId) || answered.has(String(wireId))) continue;
      if (openingOnly && !this.#store.speaksForWire(wireId, this.#clientId)) continue;
      if (skipWires?.has(String(wireId))) continue;
      if (this.#store.voiceIdForWire(wireId) !== null) continue;
      if (deckIsMaterializing(Number(wireId))) continue;
      this.#sendPlayerEvent({
        type: 'player:state',
        voiceId: String(wireId),
        // The played-card set travels on an EMPTY deck too: a player who deals a card and then ejects
        // it publishes only through here, and the card stays played.
        state: {
          playback: { isPlaying: false },
          parameters: { activeDimensionId: NO_DIMENSION, dims: {} },
          ...(hand ? { hand } : {}),
        },
      });
    }
  }

  #buildVoiceState(ctx) {
    const pm = ctx.parameterManager;
    const xParam = pm.getParameter?.('x') ?? null;
    const dims = {};
    let activeDimensionId = NO_DIMENSION;
    // getParameter() exposes `dimensions` as a PLAIN OBJECT (not the internal Map) — read it as
    // one. The old Map-API read (`dimensions.keys`) was always falsy here, so every snapshot
    // published the single-dimension shape: a rejoiner/late joiner got `main` dims and never the
    // per-dimension values or the active dimension — the restore visibly lost the dimension.
    const dimIds = xParam?.isMultidimensional ? Object.keys(xParam.dimensions ?? {}) : [];
    if (dimIds.length > 0) {
      activeDimensionId = xParam.activeDimensionId ?? NO_DIMENSION;
      for (const dimId of dimIds) {
        dims[dimId] = {};
        for (const name of ROUTED_DIMENSIONAL) {
          const v = pm.getNormalizedValue(name, dimId);
          if (Number.isFinite(v)) dims[dimId][name] = v;
        }
      }
    } else {
      dims[NO_DIMENSION] = {};
      for (const name of ROUTED_DIMENSIONAL) {
        const v = pm.getNormalizedValue(name);
        if (Number.isFinite(v)) dims[NO_DIMENSION][name] = v;
      }
    }
    const engine = ctx.audioEngine ?? null;
    const isPlaying = Boolean(engine?.playback?.isPlaying?.());
    const parameters = { activeDimensionId, dims };
    // Carry BOTH shared gain stages so a late joiner materializes the same mix, not just the axes.
    for (const paramName of GAIN_PARAMS) {
      const level = pm.getNormalizedValue?.(paramName);
      if (Number.isFinite(level)) parameters[ROUTED_PARAMS.get(paramName).stateField] = level;
    }
    const state = {
      playback: { isPlaying },
      parameters,
    };
    // Carry the deck's warp toggle (routed shared state) so a late joiner hears the same rate rule.
    if (ctx.deck) state.warp = ctx.deck.warp !== false;
    // Same for the deck's SYNC toggle — a late joiner must see who is riding the shared pulse.
    if (ctx.deck) state.sync = ctx.deck.syncEnabled === true;
    // The two shared-phase projection INPUTS (divisor + origin) and the launch grid. A late joiner
    // that computed its playhead from its own copies would sit a constant offset out — or drift
    // without limit — from the first note, which is the whole reason these are routed. Omitted when
    // the deck has no tempo yet: absent means "nothing to converge on", never "clear yours".
    if (Number.isFinite(ctx.deck?.nativeTempo)) state.nativeTempo = ctx.deck.nativeTempo;
    const downbeat = ctx.deck?.getGridMarkerTimeSec?.();
    if (Number.isFinite(downbeat)) state.downbeat = downbeat;
    if (Number.isFinite(ctx.deck?.launchGridBars)) state.launchGrid = ctx.deck.launchGridBars;
    // Carry the deck's phase ANCHOR (routed shared state) so a late joiner comes in on the room's
    // phase rather than the grid marker — omitted, never nulled, while the deck rides the default.
    // Always present (null = "no anchor, ride the grid marker"), never omitted — the same convention
    // the loop field uses. An omitted key cannot tell a receiver to DROP an anchor it is still
    // holding from an earlier session, and a stale phase survives a rejoin.
    state.seekAnchor = ctx.deck?.seekAnchor ?? null;
    // The Cosmic LFO's discrete state per axis, for the dimension that is active. Its two KNOBS
    // ride the dims above like any routed param; these three are the ones that never reached the
    // wire at all, so without them a late joiner materializes a deck whose LFO is off — or running
    // a different shape off a different body — while everyone else hears it.
    const cosmic = {};
    for (const axis of COSMIC_AXES) {
      const lfo = ctx.cosmicLFOManager?.[axis];
      if (!lfo) continue;
      cosmic[axis] = {
        enabled: lfo.isCosmicEnabled?.() === true,
        source: lfo.getFrequencySource?.() ?? null,
        waveform: lfo.getWaveform?.() ?? null,
        rate: lfo.getNoteRate?.() ?? null,
      };
    }
    if (Object.keys(cosmic).length > 0) state.cosmic = cosmic;
    // The device-sensor toggles, so a late joiner sees a lit button rather than an axis moving on
    // its own. Every deck this client holds can answer, focused or not — the switch belongs to the
    // DECK and lives in the bridge, not on the one sensor controller. The key is omitted only for a
    // deck nothing has ever been said about: absent means "nothing to say", never "switch yours
    // off", the same convention the tempo fields use.
    const sensors = voiceSensorSnapshot(ctx.id);
    if (sensors) state.sensors = sensors;
    // Carry the deck's camera focus (view mirror) so a late joiner looks at the same body.
    state.camera = getCameraFocus(ctx.id);
    // Carry the loop (routed shared state): { startMs, endMs, enabled } | null for "no loop set".
    // Only readable once the engine exists — the key is OMITTED (not nulled) before that, so a
    // pre-engine snapshot can never instruct receivers to clear a loop it simply couldn't see.
    if (engine) {
      // The loop this voice LOGICALLY has, not the momentary engine state: an out-of-loop seek
      // releases the loop for a moment, and a snapshot taken in that window would publish "no loop"
      // and delete it on every other client for good.
      state.loop = engine.getSharedLoopState?.() ?? null;
    }
    // Carry the panel mirrors (view state) so a late joiner sees the deck on the same panel.
    const panelState = voicePanelSnapshot(ctx.id);
    if (typeof panelState.panelId === 'string' && panelState.panelId.length > 0) {
      state.panel = panelState.panelId;
    }
    state.infoPanel = panelState.infoMode ?? null;
    // The selected loop subdivision (view state). Always present, never omitted — `null` is the real
    // "no preset describes this loop" value a receiver must be able to converge ON.
    state.loopSize = panelState.loopSize;
    // Which gain stage the rail shows. Omitted while nobody has chosen (the rail keeps its own
    // default) — unlike the loop size there is no meaningful "null" stage to converge on.
    if (panelState.gainChannel) state.gainChannel = panelState.gainChannel;
    return state;
  }

  // ── inbound ───────────────────────────────────────────────────────────────

  #onMessage(msg) {
    if (this.#disposed || !msg || typeof msg !== 'object') return;
    // Every fanned-out message carries the relay's clock, and this tab has no other way to read it.
    // Taking it from ALL of them, not only the parameter writes, is what keeps a hold ageing while
    // the room is doing something else — otherwise a gesture abandoned just before the traffic went
    // quiet would sit there apparently still held until the next knob moved.
    roomLeases.noteRelayClock(msg.rt);
    roomScore.noteRelayClock();
    switch (msg.type) {
      case 'sync:registered':
        // The ack carries the EFFECTIVE identity: the reclaimToken to present on the next
        // register, and possibly a DIFFERENT clientId — a reclaim the server could not prove
        // makes this tab a brand-new participant (the live seat is never rebound or evicted).
        // Adopt it: role/grants keyed to the old id simply won't match this client anymore, and
        // the next room:joined states the new truth (roles never survive a failed reclaim).
        this.#adoptIdentity(msg);
        // Fresh register handshake (initial connect or an auto-reconnect) → (re-)join the room.
        this.#sendJoin();
        break;
      case 'room:created':
        // Mint ack (creator only). Authoritative state still arrives in room:joined; seed the epoch
        // early when the ack carries it. An already-existing room answers with room:error instead
        // (a creator refresh) — non-fatal: the buffered room:join still seats this client.
        if (msg.roomId && msg.roomId !== this.#roomId) break;
        if (Number.isFinite(msg.sessionEpoch)) this.#store.update({ sessionEpoch: msg.sessionEpoch });
        break;
      case 'room:joined': {
        if (msg.roomId && msg.roomId !== this.#roomId) break;
        // We were seated — the mint handshake succeeded (fresh create OR an already-existing room).
        this.#resolveCreateHandshake();
        // A join is the one moment this tab knows it may have missed part of the stream, so the
        // fold starts over. Whether it may also have missed a HAND is a different question, and
        // the honest test is the room's phase, not this object's history: a room already PLAYING
        // was doing something before we got here, and it makes no difference whether we are a
        // reconnect, a page reload reclaiming its seat, or a latecomer — all three arrive with an
        // empty ledger into a session with hands already on controls. Joining a room still in the
        // lobby cannot have missed anything: nothing has happened yet.
        //
        // An empty ledger is not a stream saying "nothing is held" — it is a ledger with no
        // opinion, and one that answers anyway hands its own user a control someone else is
        // visibly driving. Both sides then keep extending their own holder, and there is no way
        // back. So it listens first (see `warming`).
        roomLeases.reset({ warm: msg.phase === 'playing' });
        // The reset drops the clock estimate with everything else, so hand the join's own stamp
        // straight back: it is the first thing on the stream we can vouch for, and the catch-up
        // window has to be measured from there rather than from whatever happens to arrive next —
        // which in a quiet room might be nothing at all.
        roomLeases.noteRelayClock(msg.rt);
        // Defensive: some relay versions may return the reclaim token here instead of (or as
        // well as) on the register ack — adopt from either location.
        this.#adoptIdentity(msg);
        // The relay may have disambiguated our visible name (same person in two tabs gets a
        // letter suffix, server-assigned) — adopt the room-visible truth so anything reading
        // this client's own name matches what the peers see.
        if (typeof msg.displayName === 'string' && msg.displayName.length > 0) {
          this.#displayName = msg.displayName;
        }
        this.#store.update({
          phase: msg.phase === 'playing' ? 'playing' : 'gathering',
          role: msg.role ?? null,
          grants: grantsFromWire(msg) ?? {},
          peers: Array.isArray(msg.peers) ? msg.peers : [],
          // Keep an epoch already seeded by the room:created ack when this message omits it.
          sessionEpoch: Number.isFinite(msg.sessionEpoch) ? msg.sessionEpoch : this.#store.state.sessionEpoch,
          // The relay's own account of the room's shape, and the last word on it. Everyone in the
          // room is told the same pair, so adopting it is what makes two people with differently
          // edited links render the same room. A relay that omits it leaves what we had.
          shape: readRelayShape(msg.shape) ?? this.#store.state.shape,
          // How this room begins, stated by the only thing that enforces it. A relay too
          // old to say leaves both at their defaults, which is the pre-gate behaviour exactly.
          readyGate: msg.readyGate === true,
          minPlayers: Number.isFinite(msg.minPlayers) ? Math.max(1, msg.minPlayers) : 1,
          // Our own flag comes on the join reply because `peers` is the relay's view of the OTHERS.
          // It matters on a RECLAIM: a seat resumed mid-gathering has a ready state we did not
          // choose in this tab, and the button has to show the room's answer, not a fresh false.
          ready: msg.ready === true,
          // Why we hold no seat, when we hold none. Only the join reply carries it — that is the
          // moment the answer is surprising.
          listenerReason: msg.listenerReason === 'account-seated' ? 'account-seated' : null,
          // The room's card draw and the pool it deals from, as the relay replays them. This is
          // the ONLY way a link-joiner learns either: they hold a grant id, and there is nothing
          // to ask about it. The creator's own answer comes back here too, which is what makes
          // one list the room's list rather than each client's reading of its own. A relay too old
          // to say leaves what we had, so an older room deals the whole collection exactly as before.
          draw: msg.draw && typeof msg.draw === 'object' ? msg.draw : this.#store.state.draw,
          pinned: Array.isArray(msg.pinned) ? normalizeDrawPinned(msg.pinned) : this.#store.state.pinned,
        });
        roomScore.applyWire(msg);
        // Beat-lock FIRST, then the snapshot — the relay's per-voice state is the authority, so it
        // must land last. The other order let the blanket "everyone joins the pulse" overwrite a
        // holder's deliberate sync-off that the snapshot had just restored, leaving this client
        // bar-quantizing a voice every peer still renders as free.
        if (this.#store.state.phase === 'playing') this.#enterPlaying();
        // Where chat starts, decided at the one moment a tab is told what it walked into. A room
        // that can reopen chat gets whatever this device remembers — that is the whole point of
        // remembering, and a reload is not an arrival. A room that cannot gets it open, because
        // there a closed rail is not closed, it is gone. Asked on every arrival, not just at
        // import: a tab that leaves one room for another never reloads that module.
        if (this.#chatCanBeReopened()) restoreChatPanelFromMemory();
        else openChatPanelWithoutRemembering();
        if (msg.playerStates && typeof msg.playerStates === 'object') {
          for (const [wireId, state] of Object.entries(msg.playerStates)) {
            this.#ingestWireState(wireId, state);
          }
        }
        this.#chat.applyBacklog(msg.chatBacklog);
        break;
      }
      case 'room:started':
        roomScore.applyWire(msg);
        this.#store.update({
          phase: 'playing',
          grants: grantsFromWire(msg) ?? this.#store.state.grants,
          // Readiness was a question about starting, and it has been answered.
          ready: false,
        });
        // ...and so has everyone else's. The relay clears its members' flags at start but announces
        // it by omission, so the peer list has to be brought along by hand or it keeps insisting the
        // room is full of people waiting to begin a session that already began.
        this.#store.clearPeerReady();
        // Chat opened itself on arrival because the waiting room is a room full of people waiting to
        // talk to each other. The Playground is not — the decks are what you came for, and a rail
        // sitting over them by default is something everyone has to close before they can play.
        // Collapsed rather than unmounted: the bubble is still there, and reopening is one press.
        // Done here rather than in the rail so it happens once, on the transition, instead of on
        // every render of a screen that must still let you open chat whenever you like.
        //
        // Only where that "one press" actually exists. A room with no score never mounts the bar the
        // bubble lives in, so closing chat there would put it out of reach for the rest of the
        // session — and now that the flag is remembered, for every later session on this device too.
        // `applyWire` above is what settles whether this room has a score, so it is safe to ask here.
        if (this.#chatCanBeReopened()) setChatPanelOpen(false);
        this.#enterPlaying();
        // Publish what we SPEAK for, now that we hold grants. The projection inputs (native tempo, downbeat)
        // are resolved per USER — a personal track-settings override means two players can boot the
        // SAME track on different values — so a room that starts with decks already dealt would
        // otherwise run from note one with each client dividing the shared beat by its own number, and
        // nothing would correct it until somebody happened to edit one. This is also what carries a
        // selection (panel, loop size) made before Start, when publishing was still closed.
        this.publishOwnStates({ openingOnly: true });
        break;
      case 'room:score-transport':
        roomScore.applyWire(msg);
        // Keep the compatibility snapshot current for readers that inspect state.grants directly.
        this.#store.update({ grants: grantsFromWire(msg) ?? this.#store.state.grants });
        // One deterministic current holder per deck refreshes the relay's full snapshot at every
        // score move. That makes the score boundary itself durable for later joiners without every
        // shared holder racing to publish a different local projection.
        this.publishOwnStates({ openingOnly: true });
        break;
      case 'room:peer-joined':
        this.#store.upsertPeer(msg.peer);
        break;
      case 'room:peer-ready':
        // Includes US: the relay fans this to the sender too, so our own button follows the room's
        // record rather than an optimistic local flip that a dropped message would leave lying.
        if (msg.peer?.clientId === this.#clientId) this.#store.update({ ready: msg.peer.ready === true });
        else this.#store.upsertPeer(msg.peer);
        break;
      case 'room:peer-role-changed':
        // A member's role or room-visible name changed without a join/leave: pre-Start spectator
        // promotion / role recompute / creator handoff, or a rename after a late account
        // resolution. When it's about US, adopt role + name like room:joined does.
        if (!msg.peer?.clientId) break;
        if (msg.peer.clientId === this.#clientId) {
          if (typeof msg.peer.displayName === 'string' && msg.peer.displayName.length > 0) {
            this.#displayName = msg.peer.displayName;
          }
          if (msg.peer.role) this.#store.update({ role: msg.peer.role });
          break;
        }
        this.#store.upsertPeer(msg.peer);
        break;
      case 'room:peer-left':
        this.#store.removePeer(msg.peer?.clientId);
        if (msg.peer?.clientId) {
          this.#seqTracker.forget(msg.peer.clientId);
          this.#dropDeckHolder(msg.peer.clientId);
          // Someone who detached cannot let go of a control. Their holds would otherwise sit out
          // their full window on a knob nobody is touching — short, but long enough to feel like
          // the room is stuck when a player closes their tab mid-gesture.
          roomLeases.forget(msg.peer.clientId);
        }
        break;
      case 'room:handover':
        // Grace expired for a leaver — the relay redistributed their voices.
        {
          roomScore.applyWire(msg);
          const grants = grantsFromWire(msg);
          if (grants) this.#store.update({ grants });
        }
        // A voice that just landed here has a NEW holder, but the relay still stores the LEAVER's
        // last snapshot for it — which the next late joiner would materialize. Republish so the
        // stored copy comes from the client that actually owns the deck now.
        this.publishOwnStates({ openingOnly: true });
        break;
      case 'room:error':
        // A refusal carrying one of our msgIds marks that chat message failed.
        if (msg.msgId) {
          this.#chat.handleError(msg);
          break;
        }
        // During the mint handshake a bare refusal is ambiguous: the FIRST may just be the mint of
        // an already-existing room (a creator refresh), which the buffered join still seats. A
        // SECOND refusal means the join was also rejected — the room isn't joinable; fail now.
        if (this.#createPending) {
          // A room with no free seat is a DIFFERENT failure from a room that
          // will not open, and it is the one a matchmaking arrival is most
          // likely to hit: the seat went while they were walking over.
          //
          // Someone SENT here to play is handed back to the page that sent them,
          // which still holds their filters and can offer the next candidate.
          // Tearing down in place instead would drop them into a solo session
          // they never asked for and quietly end a search that was working — the
          // exact dead end the shortlist exists to avoid.
          if (msg.code === 'ROOM_FULL') {
            this.#failAndTeardown('room-full');
            if (isMatchmakingArrival() && typeof window !== 'undefined') {
              console.warn('[room] no free seat — returning to the search');
              // `back` rather than a hard-coded path: the search page is exactly
              // where this tab came from, and it is restored with its chips as
              // they were left.
              if (window.history.length > 1) window.history.back();
              break;
            }
            console.warn('[room] no free seat in this room — leaving room mode');
            break;
          }
          if (this.#createErrorSeen) {
            console.warn('[room] room create and join both refused — leaving room mode', msg.code ?? '');
            this.#failAndTeardown('create-failed');
            break;
          }
          this.#createErrorSeen = true;
          console.warn('[room] room create refused (may be a refresh) — awaiting join', msg.code ?? '');
          break;
        }
        // The relay refused something outside the handshake. Only a few codes
        // mean the PLATFORM failed — the rest are the relay's rules working, and
        // marking those as trouble would let one rate-limited chat message void
        // a whole room's reliability evidence. `noteRelayError` holds the list.
        this.#outcome.noteRelayError(msg.code);
        console.warn('[room] relay error:', msg.code, msg.detail ?? '');
        break;
      case 'chat:message':
        this.#chat.receive(msg);
        break;
      case 'sync:connection-lost':
        // The socket gave up for good — release everything this client holds (gates, covers,
        // subscriptions) so the realm is usable solo instead of locked to a dead room.
        //
        // And this specifically is a PLATFORM issue, not a departure. It is the
        // one case where "they vanished" and "their network died" are the same
        // event seen from two sides, and the person whose connection dropped is
        // in no position to say so — so the side that CAN say so says it.
        this.#outcome.notePlatformIssue();
        console.warn('[room] connection lost — leaving room mode');
        this.dispose();
        break;
      case 'player:param':
        this.#onPlayerEvent(msg);
        break;
      case 'player:state':
        this.#onPlayerState(msg);
        break;
      case 'player:states':
        // The relay's answer to our gap-driven player:state-request: its stored snapshots, one per
        // deck, sent only to this socket.
        //
        // Decks this client drives used to be skipped wholesale, on the grounds that our live state
        // was the newer one. That only held while a deck had a single driver: we are asking because
        // we MISSED something, and what we missed may be exactly another driver's edit to a deck we
        // drive too — so those are repaired now.
        //
        // The one thing still skipped is a deck this hand is ON. The stored copy is at best as old
        // as our last published edit, and our own refresh of it is still debounced, so applying it
        // mid-gesture would drag the control backwards under the finger and then republish the
        // rollback. A gesture settles in well under a second; the next refresh publishes the truth.
        if (msg.playerStates && typeof msg.playerStates === 'object') {
          for (const [wireId, state] of Object.entries(msg.playerStates)) {
            if (this.#editedRecently(wireId)) {
              // Applying this now would drag the control backwards under the finger. Keep it until
              // the gesture settles — dropping it would lose the very thing we asked for.
              this.#deferredStates.set(String(wireId), state);
              this.#scheduleStateRefresh();
              continue;
            }
            this.#ingestWireState(wireId, state);
          }
        }
        break;
      case 'player:state-request':
        // A peer detected a gap in OUR stream — answer with fresh snapshots of our voices.
        if (msg.clientId === this.#clientId) this.publishOwnStates();
        break;
      case 'player:load-ready':
        // A participant's readiness ack for a deck load (from ANY participant — the ack is about THEIR
        // readiness). Server-stamped `from` when present, else the sender's declared clientId.
        this.#recordDeckAck(Number(msg.voiceId), msg.loadId, msg.from?.clientId ?? msg.clientId);
        break;
      case 'player:load-failed':
        // A load failed somewhere — don't block the barrier forever; release with a warning. Scoped
        // to the load it names, so a late verdict from a superseded load leaves the current one armed.
        this.#releaseDeck(Number(msg.voiceId), 'failed', msg.loadId ?? null);
        break;
      default:
        break;
    }
  }

  /** Adopt + persist the identity a relay ack carries (effective clientId and/or reclaim token). */
  #adoptIdentity(msg) {
    const clientId = typeof msg.clientId === 'string' && msg.clientId.length > 0 ? msg.clientId : null;
    const reclaimToken = typeof msg.reclaimToken === 'string' && msg.reclaimToken.length > 0
      ? msg.reclaimToken
      : null;
    if (!clientId && !reclaimToken) return;
    if (clientId && clientId !== this.#clientId) {
      // Every ownership answer, echo check, and publish gate reads #clientId live, so the switch
      // takes effect immediately; covers re-derive on the next store change (room:joined follows).
      this.#clientId = clientId;
      // The old seat's role belongs to whoever still holds the old id — drop it NOW rather than
      // let e.g. a creator's Start fire in the gap before room:joined states the new truth.
      this.#store.update({ role: null, selfClientId: clientId });
    }
    try {
      this.#persistIdentity({ clientId: clientId ?? undefined, reclaimToken: reclaimToken ?? undefined });
    } catch (error) {
      console.warn('[room] persisting identity failed', error);
    }
  }

  /**
   * Which CONNECTION the sender is speaking on matters as much as who they are: a seat outlives a
   * socket, and each new socket numbers its messages from one again. The relay stamps that count so
   * a restarted stream is recognised as one, rather than read as thousands of duplicates and
   * discarded — which would happen on the peers that were connected through the rebind, and not on
   * the ones that were not.
   */
  #trackSender(msg) {
    const senderId = msg.from?.clientId;
    if (!senderId) return 'ok';
    const result = this.#seqTracker.track(senderId, msg.from?.streamId ?? 0, msg.seq);
    if (result === 'gap') {
      // Missed messages from this sender → ask for a state refresh; the current message still applies.
      this.#adapter.sendRaw({ type: 'player:state-request', roomId: this.#roomId, clientId: senderId });
    }
    return result;
  }

  #onPlayerEvent(msg) {
    const mine = msg.from?.clientId === this.#clientId;
    // A duplicate is folded nowhere: it carries an older relay stamp than the stream has already
    // reached, and taking it would age an open hold backwards. Our own echo can't be one — we do
    // not number ourselves, and the relay hands our messages back in the order it took them.
    if (!mine && this.#trackSender(msg) === 'stale') return;

    // Leases fold from EVERY write, our own echo INCLUDED — that is the whole reason the relay
    // sends it back. A client that skipped its own messages would be folding a shorter stream than
    // everyone else and would be the one that disagreed about who is driving.
    const holdersWrite = this.#foldLease(msg);

    if (mine) return;
    // Another hand is on that control. Every client refuses the same write, so the value they all
    // keep is the holder's, and the loser of a simultaneous grab sees their own knob snap back.
    if (!holdersWrite) return;

    if (msg.bundle === true && Array.isArray(msg.messages)) {
      for (const m of msg.messages) this.#applyAddress(m?.address, m?.args, msg.from?.clientId ?? null);
      return;
    }
    this.#applyAddress(msg.address, msg.args, msg.from?.clientId ?? null);
    const parsed = parseAddress(msg.address);
    if (
      parsed?.kind === 'param'
      && !this.#store.ownsWire(parsed.wireVoiceId, msg.from?.clientId)
      && this.#store.speaksForWire(parsed.wireVoiceId, this.#clientId)
    ) {
      // A revoked holder may legally finish the hand it opened before the section boundary. The
      // current section's speaker has just applied that final value, so let THEM refresh the full
      // deck snapshot; the revoked sender no longer owns this wire and cannot do it themselves.
      this.#scheduleStateRefresh();
    }
  }

  /**
   * Fold one relayed player event into the lease ledger.
   *
   * Only a single-address PARAMETER write is a gesture. A BUNDLE is a dimension switch published
   * atomically with the new dimension's values — one discrete act on the whole voice, not a hand on
   * one control — and everything else on this path (transport, loop, load…) is discrete too, so
   * they all pass through untouched and keep last-write-wins.
   *
   * @returns {boolean} whether this write belongs to the control's holder.
   */
  #foldLease(msg) {
    if (msg.bundle === true) return true;
    const parsed = parseAddress(msg.address);
    if (parsed?.kind !== 'param') return true;
    // A marked generator write is not a gesture: it opens no hold and — critically — ends none,
    // where an ordinary `commit` fold would close the sender's own open hold on the control the
    // oscillator happens to share. It still yields to a hand: held by someone else, every client
    // (the sender's own echo included) refuses the sample the same way.
    if (msg.generator === true) {
      return roomLeases.foldGenerator({
        wireVoiceId: parsed.wireVoiceId,
        paramName: parsed.axis,
        dimensionId: parsed.dimensionId,
        clientId: msg.from?.clientId,
        rt: Number(msg.rt),
      });
    }
    // Keyed by the WIRE id straight off the address, deliberately NOT translated to a runtime voice
    // first: a deck materializes at a different moment on every machine, so a client still loading
    // would skip events its peers folded, and from then on the two are folding different streams.
    return roomLeases.fold({
      wireVoiceId: parsed.wireVoiceId,
      paramName: parsed.axis,
      dimensionId: parsed.dimensionId,
      clientId: msg.from?.clientId,
      intent: msg.intent === 'live' ? 'live' : 'commit',
      rt: Number(msg.rt),
    });
  }

  #onPlayerState(msg) {
    if (msg.from?.clientId === this.#clientId) return;
    if (this.#trackSender(msg) === 'stale') return;
    this.#ingestWireState(msg.voiceId, msg.state);
  }

  /**
   * Take a wire-addressed snapshot: materialize the deck's card (so a late joiner's empty deck loads)
   * — or, when the snapshot carries NO deck, empty ours (a resync after a missed clear must converge
   * to the owner's truth, and an already-empty deck no-ops). Then apply the param/playback state. If
   * no runtime voice maps to that wire yet (the deck is still booting from the load we just kicked
   * off), hold the state by wire id until one does.
   */
  #ingestWireState(wireId, state) {
    if (!state || typeof state !== 'object') return;
    // Before the deck itself: a snapshot's card is one of the cards that were PLAYED, and applying
    // the deck re-runs the load path, which consumes it again anyway. Folding first keeps the two
    // in step whichever way the snapshot arrives.
    if (state.hand) applyRemoteHandConsumed(state.hand);
    if (state.deck) applyRemoteDeckLoad(Number(wireId), state.deck);
    else applyRemoteDeckClear(Number(wireId));
    const voiceId = this.#store.voiceIdForWire(wireId);
    if (voiceId) this.#stageVoiceState(voiceId, state);
    else this.#pendingWireStates.set(String(wireId), state);
  }

  /** Move any wire-held snapshot to per-voice staging once its deck's runtime voice has mapped in. */
  #drainPendingWireStates() {
    for (const [wireId, state] of [...this.#pendingWireStates]) {
      const voiceId = this.#store.voiceIdForWire(wireId);
      if (voiceId) {
        this.#pendingWireStates.delete(wireId);
        this.#stageVoiceState(voiceId, state);
      }
    }
  }

  #applyAddress(address, args, fromClientId = null) {
    const parsed = parseAddress(address);
    if (!parsed) return;
    if (parsed.kind === 'session-tempo') {
      const bpm = Number(args?.[0]);
      // Tempo also replicates over the pulse; this path exists for holder writes on the event
      // layer. The coordinator's epsilon guard makes the overlap harmless.
      if (Number.isFinite(bpm) && bpm > 0) syncCoordinator.setTempo(bpm, { sourceType: 'remote' });
      return;
    }
    if (parsed.kind === 'load') {
      // A structural deck load command from the deck's owner: the wire id IS the deck/stage index. Arm
      // the barrier for this loadId (absent on a world swap — visuals only, no barrier), then apply
      // through the collection app's manual card-load/swap path (empty decks have no runtime voice, so
      // this never needs the voice map).
      const stageIndex = Number(parsed.wireVoiceId);
      const ref = args?.[0];
      if (!Number.isInteger(stageIndex)) return;
      if (ref && typeof ref === 'object') {
        const { loadId = null, ...entryRef } = ref;
        if (loadId) this.#armDeck(stageIndex, loadId);
        // Somebody PLAYED this card — said here, where a command is unmistakably a command. The
        // applier below also runs for deck snapshots, which describe what a deck holds rather than
        // what was just played, so it is the wrong place to decide a card has left the hand.
        applyRemoteCardPlayed(entryRef);
        applyRemoteDeckLoad(stageIndex, entryRef, loadId);
      } else if (ref == null) {
        // A deck CLEAR ("load nothing"): empty the deck locally and release any in-flight barrier —
        // the load it was arming no longer has anything to become playable.
        this.#releaseDeck(stageIndex, 'cleared');
        applyRemoteDeckClear(stageIndex);
      }
      return;
    }
    const voiceId = this.#store.voiceIdForWire(parsed.wireVoiceId);
    // Panel mirrors are a VIEW concern applied by EVERYONE (players + spectators), through the panel
    // bridge keyed by voiceId — they need no audio ctx, so handle them before the ctx guard.
    if (parsed.kind === 'panel') {
      if (voiceId) applyRemotePanel(voiceId, args?.[0]);
      return;
    }
    if (parsed.kind === 'info-panel') {
      if (voiceId) applyRemoteInfo(voiceId, args?.[0] ?? null);
      return;
    }
    if (parsed.kind === 'gain-channel') {
      // A view mirror like the panel: applied by EVERYONE, through the bridge (no audio ctx needed,
      // and it must reach a voice whose rail has not mounted yet) — hence before the ctx guard.
      if (voiceId) applyRemoteGainChannel(voiceId, args?.[0]);
      return;
    }
    if (parsed.kind === 'loop-size') {
      // A view mirror like the panel: applied by EVERYONE, and by the bridge — not the engine — so it
      // reaches a deck whose waveform panel is closed (and needs no audio ctx, hence before the guard).
      if (voiceId) applyRemoteLoopSize(voiceId, Number(args?.[0] ?? NaN));
      return;
    }
    if (parsed.kind === 'camera') {
      // Same view-mirror class as the panel: everyone (players + spectators) follows the deck
      // owner's focus choice. The latch keeps our own subscription from echoing it back out.
      const focus = args?.[0];
      if (voiceId && (focus === 'world' || focus === 'moon')) {
        this.#withRemoteApply(() => setCameraFocus(voiceId, focus));
      }
      return;
    }
    if (parsed.kind === 'sensor') {
      // ABOVE the ctx guard, with the other mirrors the bridges hold: the sensor switch lives in
      // `sensorSwitches` keyed by voiceId and needs no audio context. Below it, a deck still
      // booting when a peer pressed would drop that press for good — nothing re-requests it — and
      // that client alone would show a dark toggle while everyone else saw it lit.
      this.#applyRemoteSensor(voiceId, parsed.axis, args?.[0], fromClientId);
      return;
    }
    const ctx = voiceId ? this.#registry.get(voiceId) : null;
    if (!ctx) return;
    if (parsed.kind === 'param') {
      if (GAIN_PARAMS.includes(parsed.axis)) this.#applyRemoteGain(ctx, parsed.axis, Number(args?.[0]));
      else this.#applyRemoteParam(ctx, parsed.axis, parsed.dimensionId, Number(args?.[0]));
    } else if (parsed.kind === 'dimension') {
      this.#applyRemoteDimension(ctx, args?.[0]);
    } else if (parsed.kind === 'transport-start' || parsed.kind === 'transport-stop') {
      // A NULL beat is the sender's "fire now" (it had no joined shared clock). `Number(null)` is 0,
      // which reads as a real beat and takes the quantized branch — keep it non-finite instead.
      this.#applyRemoteTransport(ctx, parsed.kind, Number(args?.[0] ?? NaN));
    } else if (parsed.kind === 'seek') {
      this.#applyRemoteSeek(ctx, args);
    } else if (parsed.kind === 'loop-enable') {
      this.#applyRemoteLoopEnable(ctx, voiceId, args?.[0]);
    } else if (parsed.kind === 'loop-range') {
      this.#applyRemoteLoopRange(ctx, voiceId, args);
    } else if (parsed.kind === 'warp') {
      this.#applyRemoteWarp(ctx, args?.[0]);
    } else if (parsed.kind === 'sync') {
      this.#applyRemoteSync(ctx, args?.[0]);
    } else if (parsed.kind === 'native-tempo') {
      this.#applyRemoteNativeTempo(ctx, Number(args?.[0] ?? NaN));
    } else if (parsed.kind === 'downbeat') {
      this.#applyRemoteDownbeat(ctx, Number(args?.[0] ?? NaN));
    } else if (parsed.kind === 'launch-grid') {
      this.#applyRemoteLaunchGrid(ctx, Number(args?.[0] ?? NaN));
    } else if (parsed.kind === 'cosmic') {
      this.#applyRemoteCosmic(ctx, parsed.axis, parsed.field, args?.[0]);
    }
  }

  /**
   * Apply a peer's device-sensor press on one axis. Two things land, not one: the button follows
   * them, and — because they just claimed the axis with their own phone — this client's sensor
   * comes off it. The controller does that second half itself; all that is needed here is to say
   * the press was somebody else's.
   *
   * For ANY deck, focused or not: the switch belongs to the deck and lives in the bridge, so every
   * client shows every deck's switch move. Only the DEVICE is tied to the focused voice.
   */
  #applyRemoteSensor(voiceId, axis, value, fromClientId) {
    // Validate the raw path segment before it reaches anything that indexes by it.
    if (!SENSOR_AXES.includes(axis)) return;
    // Straight to the deck's own switch — no latch and no focus test. The switch belongs to the
    // deck, not to this client's sensor controller: every client must see it move, including one
    // looking at another deck and one that has never opened the Sensors panel at all.
    // Stamped with who pressed it, so the toggle can show whose device is on the axis.
    applyRemoteSensorToDeck(voiceId, axis, Number(value) === 1, fromClientId ?? null);
  }

  /**
   * Apply a peer's Cosmic LFO act on one axis. Under the remote latch, because applying any of
   * them moves the LFO's frequency or amplitude PARAMS — which are routed, and would otherwise be
   * republished as if this client had just made the change.
   *
   * The kick is sent as the STEP, not as the frequency it produces: the multiplier accumulates, so
   * the next kick is computed from it, and the resulting value rides the frequency param anyway.
   */
  #applyRemoteCosmic(ctx, axis, field, value) {
    // Check the address against what we route BEFORE indexing anything with it: the axis and the
    // field arrive as raw path segments, and a key like `__proto__` must never reach a lookup.
    if (!COSMIC_AXES.includes(axis) || !COSMIC_FIELDS.includes(field)) return;
    const lfo = ctx.cosmicLFOManager?.[axis];
    if (!lfo) return;
    this.#withRemoteApply(() => {
      if (field === 'enable') {
        if (Number(value) === 1) lfo.start?.({ roomRemote: true });
        else lfo.stop?.({ roomRemote: true });
      } else if (field === 'source' && typeof value === 'string' && value) {
        lfo.setFrequencySource?.(value, { roomRemote: true });
      } else if (field === 'waveform' && typeof value === 'string' && value) {
        lfo.setWaveform?.(value, { roomRemote: true });
      } else if (field === 'kick' && (value === 'half' || value === 'double')) {
        // The legacy trigger labels are what `triggerKick` speaks; the wire carries the meaning.
        lfo.triggerKick?.(`${axis}Cosmic${value === 'half' ? 1 : 2}`, { roomRemote: true });
      } else if (field === 'rate' && typeof value === 'string' && value) {
        lfo.setNoteRate?.(value, { roomRemote: true });
      }
    });
  }

  /**
   * Apply a remote NATIVE (audiofile) tempo. In-memory only, deliberately NOT through
   * `commitTrackBpmFromUi`: that seam also PERSISTS to the editor's per-user track settings, and a
   * peer's edit is not this user's saved preference. The deck bumps `projectionGeneration`, which is
   * half of the adapter's `_projectionToken()`, so an in-flight drift correction computed against the
   * old scale dies instead of landing on a timeline that no longer exists.
   */
  #applyRemoteNativeTempo(ctx, bpm) {
    if (!Number.isFinite(bpm) || bpm <= 0) return;
    ctx.deck?.setNativeTempo?.(bpm, { roomRemote: true });
  }

  /**
   * Apply a remote DOWNBEAT (grid marker, seconds). Through the ENGINE when there is one: the deck's
   * own change stream re-aligns only while stopped, and the engine's setter forces the re-align that a
   * marker moved mid-play needs. Pre-engine (a still-booting deck) the deck alone is correct — the
   * start that follows aligns through the new origin anyway.
   */
  #applyRemoteDownbeat(ctx, sec) {
    if (!Number.isFinite(sec)) return;
    if (typeof ctx.audioEngine?.setGridMarkerTimeSec === 'function') {
      ctx.audioEngine.setGridMarkerTimeSec(sec, { roomRemote: true });
      return;
    }
    ctx.deck?.setGridMarkerTimeSec?.(sec, { roomRemote: true });
  }

  /** Apply a remote LAUNCH GRID in bars. A room never honours 'none': the deck refuses a 0 on the
   *  SENDING side too, so no conforming peer can publish one and both sides land on the same value.
   *  Dropping it here as well only guards against a peer that doesn't enforce the rule. */
  #applyRemoteLaunchGrid(ctx, bars) {
    if (!Number.isFinite(bars) || bars <= 0) return;
    ctx.deck?.setLaunchGridBars?.(bars, { roomRemote: true });
  }

  /** Apply a remote WARP toggle to a voice's deck (no re-publish — the deck sees a remote apply). */
  #applyRemoteWarp(ctx, arg) {
    ctx.deck?.setWarp?.(Number(arg) === 1, { roomRemote: true });
  }

  /** Apply a remote SYNC toggle to a voice's deck (no re-publish — the deck sees a remote apply). */
  #applyRemoteSync(ctx, arg) {
    ctx.deck?.setSyncEnabled?.(Number(arg) === 1, { roomRemote: true });
  }

  /** Apply a remote loop ENABLE/disable to a voice's engine (no re-publish), then refresh its UI. */
  #applyRemoteLoopEnable(ctx, voiceId, arg) {
    const engine = ctx.audioEngine;
    if (!engine?.setLoopEnabled) return;
    const active = Number(arg) === 1;
    engine.setLoopEnabled(active, { roomRemote: true });
    this.#notifyLoopUi(voiceId, active);
  }

  /** Apply a remote loop RANGE (or a clear) to a voice's engine (no re-publish), then refresh its UI. */
  #applyRemoteLoopRange(ctx, voiceId, args) {
    const engine = ctx.audioEngine;
    if (!engine) return;
    const startMs = Number(args?.[0]);
    const endMs = Number(args?.[1]);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
      engine.clearLoop?.({ roomRemote: true });
      this.#notifyLoopUi(voiceId, false);
      return;
    }
    const active = Number(args?.[2]) === 1;
    engine.setLoopRange?.(startMs, endMs, { active, roomRemote: true });
    this.#notifyLoopUi(voiceId, active);
  }

  /**
   * Nudge the waveform panel + transport loop button to re-read the engine loop after a remote apply.
   * They stay linked through the shared `ui:loop-toggle` document event (stamped with the voiceId so a
   * sibling tile ignores it); `source: 'remote'` marks it external so the kit panel re-reads the range.
   */
  #notifyLoopUi(voiceId, enabled) {
    if (typeof document === 'undefined') return;
    document.dispatchEvent(new CustomEvent('ui:loop-toggle', {
      detail: { enabled, source: 'remote', voiceId },
    }));
  }

  #applyRemoteParam(ctx, axis, dimensionId, normalized) {
    const pm = ctx.parameterManager;
    if (!pm || !ROUTED_DIMENSIONAL.has(axis) || !Number.isFinite(normalized)) return;
    const param = pm.getParameter?.(axis);
    if (!param) return;
    this.#withRemoteApply(() => {
      if (param.isMultidimensional && dimensionId !== NO_DIMENSION) {
        // getParameter() exposes `dimensions` as a PLAIN OBJECT (not the internal Map) — read it as
        // one, or the per-dimension range is never seen and every remote value denormalizes against
        // the parameter-level range instead.
        const dimState = param.dimensions?.[dimensionId];
        const min = Number.isFinite(dimState?.min) ? dimState.min : param.min;
        const max = Number.isFinite(dimState?.max) ? dimState.max : param.max;
        pm.setDimensionValue(axis, dimensionId, pm.denormalize(normalized, min, max), this, REMOTE_WRITE_PRIORITY, { updateIntent: 'live' });
      } else {
        pm.setNormalizedValue(axis, normalized, this, REMOTE_WRITE_PRIORITY, { updateIntent: 'live' });
      }
    });
  }

  /** Apply a remote gain-stage value to a voice (single param, no dimension). */
  #applyRemoteGain(ctx, paramName, normalized) {
    const pm = ctx.parameterManager;
    if (!pm || !Number.isFinite(normalized)) return;
    if (!pm.getParameter?.(paramName)) return;
    this.#withRemoteApply(() => {
      pm.setNormalizedValue(paramName, normalized, this, REMOTE_WRITE_PRIORITY, { updateIntent: 'live' });
    });
  }

  #applyRemoteDimension(ctx, dimensionId) {
    if (typeof dimensionId !== 'string' || dimensionId.length === 0 || dimensionId === NO_DIMENSION) return;
    // The room now holds THIS dimension for the voice, so the publish dedup must follow it. Leaving
    // the latch on what we last published means a local switch BACK to that dimension is deduped
    // away and never reaches the room — every peer keeps rendering the dimension we moved off.
    this.#lastPublishedDim.set(ctx.id, dimensionId);
    this.#withRemoteApply(() => {
      // Prefer the mode controller (the same path local switches use — audio AND visuals follow);
      // fall back to the raw parameter store when a voice has no mode controller.
      if (typeof ctx.worldMode?.setActiveDimension === 'function') {
        ctx.worldMode.setActiveDimension(dimensionId);
      } else {
        ctx.parameterManager?.setActiveDimension?.(dimensionId);
      }
    });
  }

  #applyRemoteTransport(ctx, kind, atBeat) {
    const engine = ctx.audioEngine;
    if (!engine) return;
    const options = { roomRemote: true };
    if (Number.isFinite(atBeat)) options.atSharedBeat = atBeat;
    const run = kind === 'transport-start'
      ? () => engine.play?.(options)
      : () => engine.stop?.(options);
    this.#queueTransport(ctx.id, run);
  }

  /**
   * Apply a remote SEEK: hand the engine the phase anchor and the beat it belongs to. It rides the
   * SAME per-voice queue as start/stop — a seek that overtook the start it was meant to follow would
   * be applied against a transport that is not running yet.
   */
  #applyRemoteSeek(ctx, args) {
    const engine = ctx.audioEngine;
    if (typeof engine?.seekToMilliseconds !== 'function') return;
    // A NULL beat is the sender's "now, everywhere" — the same convention transport uses, and what an
    // unsynced deck (SYNC off, no shared pulse to wait on) sends. `Number(null)` is 0, which would
    // read as a real beat at the very start of the session, so keep it non-finite instead.
    const anchorBeat = Number(args?.[0] ?? NaN);
    const anchorSourceMs = Number(args?.[1] ?? NaN);
    // The POSITION is the part a seek cannot do without; the beat is optional.
    if (!Number.isFinite(anchorSourceMs)) return;
    this.#queueTransport(ctx.id, () => engine.seekToMilliseconds(anchorSourceMs, {
      roomRemote: true,
      atSharedBeat: Number.isFinite(anchorBeat) ? anchorBeat : null,
    }));
  }

  /** Serialize one voice's remote transport applies so the relay's order is the applied order. */
  #queueTransport(voiceId, run) {
    const prev = this.#transportQueues.get(voiceId) ?? Promise.resolve();
    const next = prev
      .then(run)
      .catch((error) => console.warn('[room] remote transport event failed', error));
    this.#transportQueues.set(voiceId, next);
  }

  #stageVoiceState(voiceId, state) {
    if (!state || typeof state !== 'object') return;
    this.#pendingStates.set(voiceId, state);
    this.#tryApplyVoiceState(voiceId);
  }

  #tryApplyVoiceState(voiceId) {
    const state = this.#pendingStates.get(voiceId);
    const ctx = this.#registry.get(voiceId);
    if (!state || !ctx?.parameterManager) return;
    const pm = ctx.parameterManager;
    this.#withRemoteApply(() => {
      const dims = state.parameters?.dims;
      if (dims && typeof dims === 'object') {
        for (const [dimId, values] of Object.entries(dims)) {
          for (const name of ROUTED_DIMENSIONAL) {
            const v = Number(values?.[name]);
            if (Number.isFinite(v) && !this.#heldByAnyone(voiceId, name, dimId)) {
              this.#applyRemoteParamDirect(pm, name, dimId, v);
            }
          }
        }
      }
      const activeDim = state.parameters?.activeDimensionId;
      if (typeof activeDim === 'string' && activeDim !== NO_DIMENSION) {
        this.#applyRemoteDimension(ctx, activeDim);
      }
      for (const paramName of GAIN_PARAMS) {
        const level = Number(state.parameters?.[ROUTED_PARAMS.get(paramName).stateField]);
        if (Number.isFinite(level) && !this.#heldByAnyone(voiceId, paramName, NO_DIMENSION)) {
          this.#applyRemoteParamDirect(pm, paramName, NO_DIMENSION, level);
        }
      }
    });
    // The Cosmic LFO's discrete state, applied AFTER the dimension switch above because all three
    // are per-dimension. Source, then waveform, then the enable: a source pick derives a fresh
    // frequency from world data, so the settings must settle before the oscillator is switched on.
    if (state.cosmic && typeof state.cosmic === 'object') {
      const activeDim = typeof state.parameters?.activeDimensionId === 'string'
        ? state.parameters.activeDimensionId
        : NO_DIMENSION;
      this.#withRemoteApply(() => {
        for (const axis of COSMIC_AXES) {
          const lfo = ctx.cosmicLFOManager?.[axis];
          const want = state.cosmic[axis];
          if (!lfo || !want || typeof want !== 'object') continue;
          // The rate lands BEFORE the source: selecting the tempo-locked source derives a
          // frequency from whatever rate is held, so arriving after it would derive once from the
          // wrong one and only correct itself on the next tempo change.
          if (typeof want.rate === 'string' && want.rate) {
            lfo.setNoteRate?.(want.rate, { roomRemote: true });
          }
          if (typeof want.source === 'string' && want.source) {
            lfo.setFrequencySource?.(want.source, { roomRemote: true });
          }
          if (typeof want.waveform === 'string' && want.waveform) {
            lfo.setWaveform?.(want.waveform, { roomRemote: true });
          }
          if (want.enabled === true) lfo.start?.({ roomRemote: true });
          else lfo.stop?.({ roomRemote: true });
        }
        // The LFO's own two params land LAST. The source pick above recomputed the frequency from
        // world data and overwrote what the snapshot applied a moment ago — and a kicked or
        // hand-dialled frequency is not recoverable from a source key.
        const values = state.parameters?.dims?.[activeDim];
        if (values && typeof values === 'object') {
          for (const name of COSMIC_PARAMS) {
            const v = Number(values[name]);
            if (Number.isFinite(v) && !this.#heldByAnyone(voiceId, name, activeDim)) {
              this.#applyRemoteParamDirect(pm, name, activeDim, v);
            }
          }
        }
      });
    }
    // The device-sensor toggles. Applied as a peer's press, which is what they are — so the button
    // arrives lit without this client's phone being asked for permission or put on the axis. The
    // player who is actually driving it is elsewhere, and their values arrive as routed axis writes.
    // A snapshot is NOT a press, and only a press may change whose device is on an axis. Two people
    // can hold one deck, and a snapshot for a deck this client also drives is applied like any other
    // (that repair is deliberate) — so an axis this player is driving right now would be handed to
    // the speaker who described it, and then nobody would be driving it at all.
    applySensorSnapshot(voiceId, state.sensors);
    // The shared-projection inputs FIRST, before anything that reads them. Native tempo especially:
    // warp below refuses to switch on for a deck with no tempo, and the anchor/loop/playback restore
    // that follows all project through the divisor and origin set here.
    if (Number.isFinite(state.nativeTempo)) this.#applyRemoteNativeTempo(ctx, state.nativeTempo);
    if (Number.isFinite(state.downbeat)) this.#applyRemoteDownbeat(ctx, state.downbeat);
    if (Number.isFinite(state.launchGrid)) this.#applyRemoteLaunchGrid(ctx, state.launchGrid);
    // Warp lives on the deck (present from voice registration — no need to wait for the engine).
    if (typeof state.warp === 'boolean') ctx.deck?.setWarp?.(state.warp, { roomRemote: true });
    if (typeof state.sync === 'boolean') ctx.deck?.setSyncEnabled?.(state.sync, { roomRemote: true });
    // The phase ANCHOR, applied BEFORE the loop and playback restore below: the restored start
    // aligns THROUGH the anchor, so it has to be in place before anything starts. (It lives on the
    // deck, which exists from voice registration — no need to wait for the engine.)
    if ('seekAnchor' in state) {
      const anchor = state.seekAnchor;
      if (anchor && Number.isFinite(anchor.beat) && Number.isFinite(anchor.sourceMs)) {
        ctx.deck?.setSeekAnchor?.(anchor.beat, anchor.sourceMs);
      } else {
        // The owner rides the grid marker — converge, or a phase from an earlier session survives.
        ctx.deck?.clearSeekAnchor?.();
      }
    }
    // Camera focus is pure view state — apply it as soon as the voice exists (latched, no echo).
    if (state.camera === 'world' || state.camera === 'moon') {
      this.#withRemoteApply(() => setCameraFocus(voiceId, state.camera));
    }
    // Panel mirrors are view state too — the bridge holds them for a still-mounting tile.
    if (typeof state.panel === 'string' && state.panel.length > 0) {
      applyRemotePanel(voiceId, state.panel);
    }
    if ('infoPanel' in state) applyRemoteInfo(voiceId, state.infoPanel ?? null);
    // Loop + playback need the engine; keep the snapshot staged until it lands, then finish.
    const engine = ctx.audioEngine;
    if (engine) {
      // Loop FIRST, so a restored start already plays under the shared loop. `loop: null` means
      // the owner has no loop — converge (an engine with none engaged no-ops); an ABSENT key is
      // an older/pre-engine snapshot and must not touch the local loop.
      if (state.loop && Number.isFinite(state.loop.startMs) && Number.isFinite(state.loop.endMs)) {
        engine.setLoopRange?.(state.loop.startMs, state.loop.endMs, {
          active: state.loop.enabled === true,
          roomRemote: true,
        });
        this.#notifyLoopUi(voiceId, state.loop.enabled === true);
      } else if (state.loop === null) {
        engine.clearLoop?.({ roomRemote: true });
        this.#notifyLoopUi(voiceId, false);
      }
      const wantPlaying = state.playback?.isPlaying === true;
      const isPlaying = Boolean(engine.playback?.isPlaying?.());
      if (wantPlaying && !isPlaying) this.#applyRemoteTransport(ctx, 'transport-start', NaN);
      else if (!wantPlaying && isPlaying) this.#applyRemoteTransport(ctx, 'transport-stop', NaN);
      // Playing on BOTH sides: the start/stop rules above have nothing to do, yet this voice is
      // free-running on whatever phase it had before the snapshot arrived. Converge it through the
      // anchor once, or a late joiner that was already playing never adopts the room's phase.
      // `correction: false` — this is authoritative ROOM state being applied, not a drift
      // correction, so the debug kill switch must never be able to strand a client off-phase.
      else if (wantPlaying && isPlaying) {
        this.#queueTransport(voiceId, () => engine.realignToSharedPhase?.({ correction: false }));
      }
      this.#pendingStates.delete(voiceId);
    }
    // The loop-subdivision mirror LAST: the loop restore above nudges the waveform panel to re-read,
    // and that re-read clears the selection (a loop that changed elsewhere matches no preset). Applying
    // the owner's selection before it would simply be wiped.
    if ('loopSize' in state) applyRemoteLoopSize(voiceId, Number(state.loopSize ?? NaN));
    if (state.gainChannel) applyRemoteGainChannel(voiceId, state.gainChannel);
  }

  /**
   * Is ANY hand on this control — including ours?
   *
   * Asked before a stored snapshot restores a value. A snapshot is a picture of a deck taken up to
   * a second ago and republished by whichever holder speaks for that voice; a hold is a hand that
   * is on the control right now. Letting the older picture win would undo the live gesture and then
   * republish the rollback, which is the knob-fight the lease was meant to end arriving by a
   * different door. Unlike the write gate this includes our OWN holds: the picture is stale for
   * every hand, not only other people's.
   */
  #heldByAnyone(voiceId, paramName, dimensionId) {
    return roomLeases.holderOf(voiceId, paramName, dimensionId) !== null;
  }

  #applyRemoteParamDirect(pm, axis, dimensionId, normalized) {
    const param = pm.getParameter?.(axis);
    if (!param) return;
    if (param.isMultidimensional && dimensionId !== NO_DIMENSION) {
      // Plain object, not the internal Map — same read as #applyRemoteParam.
      const dimState = param.dimensions?.[dimensionId];
      const min = Number.isFinite(dimState?.min) ? dimState.min : param.min;
      const max = Number.isFinite(dimState?.max) ? dimState.max : param.max;
      pm.setDimensionValue(axis, dimensionId, pm.denormalize(normalized, min, max), this, REMOTE_WRITE_PRIORITY, { updateIntent: 'live' });
    } else {
      pm.setNormalizedValue(axis, normalized, this, REMOTE_WRITE_PRIORITY, { updateIntent: 'live' });
    }
  }

  #withRemoteApply(fn) {
    this.#applyingRemote = true;
    try {
      fn();
    } finally {
      this.#applyingRemote = false;
    }
  }

  // ── voice wiring ──────────────────────────────────────────────────────────

  #syncVoices() {
    const present = new Set();
    for (const ctx of this.#registry.all()) {
      if (!ctx?.id) continue;
      present.add(ctx.id);
      if (!this.#voices.has(ctx.id)) this.#attachVoice(ctx);
    }
    for (const voiceId of [...this.#voices.keys()]) {
      if (!present.has(voiceId)) this.#detachVoice(voiceId);
    }
    // A deck's voice may register AFTER its wire mapping was set (load → map → boot) — drain any
    // snapshot that was waiting on it.
    this.#drainPendingWireStates();
  }

  #attachVoice(ctx) {
    const voiceId = ctx.id;
    const pm = ctx.parameterManager;
    if (!pm || this.#store.wireIdForVoice(voiceId) === null) return;

    // Publisher: one subscriber object per voice; the manager itself skips notifying the source of
    // a write, and the remote-apply latch silences everything applied from the network.
    const client = this;
    const controller = {
      onParameterChanged(name, _value, dimensionId, metadata) {
        client.#onLocalParam(voiceId, pm, name, dimensionId, metadata);
      },
    };
    // Every param the table routes — the axes, the Cosmic knobs, and both gain stages.
    //
    // A deck attaches the moment its voice registers, which is BEFORE that voice has hydrated its
    // parameters from track data. The manager parks a subscription taken out on a parameter that
    // does not exist yet and attaches it at registration, so this publisher is live from the
    // parameter's first value either way — and a snapshot built inside that window simply omits
    // what has not been registered rather than describing it with an invented one.
    // Guarded twice, because subscribing is no longer inert: for a param that IS registered — the
    // ordinary case of joining a room whose decks are loaded — it replays through this controller
    // synchronously. A manager that cannot subscribe, or a replay that throws, must cost this voice
    // its publisher and nothing more. Escaping here would abandon the voice half-wired (subscribed,
    // but with no write gate, no deck bridges, and absent from `#voices`, so no detach can ever
    // clean it up) and abort the attach for every voice behind it.
    if (typeof pm.subscribe === 'function') {
      for (const name of ROUTED_PARAMS.keys()) {
        try { pm.subscribe(controller, name); } catch (error) {
          console.warn('[room] could not subscribe voice publisher', { voiceId, name, error });
        }
      }
    }
    // Soft write gate: interactive writes on a voice this client may not edit are dropped at the
    // store (system writes — null source — and remote applies always pass; the relay is the real gate).
    pm.setWriteGate?.((paramName, source, dimensionId) =>
      this.#allowLocalWrite(voiceId, paramName, source, dimensionId));
    // Interactive dimension switches (keyboard / MIDI / selector all route through the mode
    // controller) follow the same ownership rule; remote-driven switches pass via the apply latch.
    ctx.worldMode?.setDimensionGate?.(
      () => this.#applyingRemote || this.#store.canEditVoice(voiceId, this.#clientId),
    );
    // The warp bridge lives on the DECK (warp's one owner — it survives engine rebuilds): a granted
    // player's toggle publishes, everyone applies, a non-holder's input is gated before it touches
    // the rate owners. Same shape as the engine's loop bridge.
    ctx.deck?.setRoomWarpBridge?.({
      canControl: () => this.#store.canEditVoice(voiceId, this.#clientId),
      publish: (on) => this.#publishWarp(voiceId, on),
    });
    // SYNC rides the same deck-owned bridge as warp: the holder decides whether this voice
    // follows the shared pulse, and everyone renders that decision the same way.
    ctx.deck?.setRoomSyncBridge?.({
      canControl: () => this.#store.canEditVoice(voiceId, this.#clientId),
      publish: (on) => this.#publishSync(voiceId, on),
    });
    // The shared-projection inputs the deck owns (native tempo, downbeat, launch grid). Same bridge
    // contract as warp/sync — these are not a mirroring nicety: the first two are the divisor and the
    // origin of the beat→playhead projection, so two clients holding different values compute
    // different positions from the same beat and never converge.
    ctx.deck?.setRoomGridBridge?.({
      canControl: () => this.#store.canEditVoice(voiceId, this.#clientId),
      publishNativeTempo: (bpm) => this.#publishNativeTempo(voiceId, bpm),
      publishDownbeat: (sec) => this.#publishDownbeat(voiceId, sec),
      publishLaunchGrid: (bars) => this.#publishLaunchGrid(voiceId, bars),
    });
    this.#voices.set(voiceId, { ctx, controller, cover: null });
    // The Cosmic LFO wiring — after the voice is known, because both halves walk the attached
    // voices. It is deliberately NOT a one-shot: see `#refreshCosmicWiring`.
    this.#refreshCosmicWiring();
    if (ctx.audioEngine) this.#wireEngine(voiceId, ctx.audioEngine);
    if (this.#store.state.phase === 'playing') this.#enterPlayingForVoice(voiceId);
    this.#tryApplyVoiceState(voiceId);
    this.#refreshVoiceCovers();
  }

  #detachVoice(voiceId) {
    const rec = this.#voices.get(voiceId);
    if (!rec) return;
    const pm = rec.ctx.parameterManager;
    // Unsubscribe exactly what the attach subscribed — the SAME table both ways. (Unsubscribing
    // from a partial list once left six subscriptions per voice on a manager that outlives the room.)
    for (const name of ROUTED_PARAMS.keys()) {
      try { pm?.unsubscribe?.(rec.controller, name); } catch { /* already gone */ }
    }
    pm?.setWriteGate?.(null);
    rec.ctx.worldMode?.setDimensionGate?.(null);
    // Hand the user's own launch grid back — the 'none' override was the room's, not theirs. A restore
    // is not a user pick, so it must not publish: `roomRemote` (the bridge is still installed here).
    if (this.#preRoomLaunchGrid.has(voiceId)) {
      const bars = this.#preRoomLaunchGrid.get(voiceId);
      this.#preRoomLaunchGrid.delete(voiceId);
      try { rec.ctx.deck?.setLaunchGridBars?.(bars, { roomRemote: true }); } catch { /* deck already gone */ }
    }
    rec.ctx.deck?.setRoomWarpBridge?.(null);
    rec.ctx.deck?.setRoomSyncBridge?.(null);
    rec.ctx.deck?.setRoomGridBridge?.(null);
    for (const axis of COSMIC_AXES) rec.ctx.cosmicLFOManager?.[axis]?.setRoomBridge?.(null);
    this.#cosmicGenerators.delete(voiceId);
    this.#cosmicBridged.delete(voiceId);
    rec.ctx.audioEngine?.setRoomTransportBridge?.(null);
    rec.ctx.audioEngine?.setRoomLoopBridge?.(null);
    rec.cover?.remove?.();
    this.#voices.delete(voiceId);
    this.#lastPublishedDim.delete(voiceId);
    this.#pendingStates.delete(voiceId);
    this.#transportQueues.delete(voiceId);
    forgetVoiceViewState(voiceId); // the remembered selection outlives the PANEL, not the voice
    // The deck's sensor switches are NOT forgotten here: a detach also happens when the room ends
    // with the deck still playing, and its switches are then this client's own. The store drops
    // them itself when the voice leaves the registry.
    // Every card deal mints a FRESH voice id, so per-voice bookkeeping left behind here is unreachable
    // for the client's whole life. The live-publish thinning map is keyed `${voiceId}/${axis}/${dim}`.
    const goneWire = this.#store.wireIdForVoice(voiceId);
    if (goneWire !== null) {
      this.#lastLocalEditAt.delete(String(goneWire));
      this.#deferredStates.delete(String(goneWire));
    }
    for (const key of this.#lastLiveSentAt.keys()) {
      if (key.startsWith(`${voiceId}/`)) this.#lastLiveSentAt.delete(key);
    }
    for (const key of this.#lastGenSentAt.keys()) {
      if (key.startsWith(`${voiceId}/`)) this.#lastGenSentAt.delete(key);
    }
  }

  #onEngineAssigned(ctx) {
    if (!ctx?.id || !this.#voices.has(ctx.id)) return;
    this.#wireEngine(ctx.id, ctx.audioEngine);
    // A deck's engine lands well after it registers, so this is the first hook that reliably sits
    // past the rest of its boot — including its Cosmic LFOs (see `#refreshCosmicWiring`).
    this.#refreshCosmicWiring();
    this.#tryApplyVoiceState(ctx.id);
  }

  #wireEngine(voiceId, engine) {
    engine?.setRoomTransportBridge?.({
      // Own the deck AND it must be past its load barrier — a deck still arming is not playable until
      // every holder has acked the load (the room is beat-locked, so a half-loaded start desyncs).
      canControl: () => this.#store.canEditVoice(voiceId, this.#clientId) && this.#deckReadyForVoice(voiceId),
      publishStart: (atBeat) => this.#publishTransport(voiceId, 'start', atBeat),
      publishStop: (atBeat) => this.#publishTransport(voiceId, 'stop', atBeat),
      publishSeek: (anchorBeat, anchorSourceMs) => this.#publishSeek(voiceId, anchorBeat, anchorSourceMs),
    });
    // The loop bridge: a holder's loop edit publishes to the room and everyone applies it (the
    // shared loop is shared state); a non-holder's edit is gated before it touches audio.
    engine?.setRoomLoopBridge?.({
      canControl: () => this.#store.canEditVoice(voiceId, this.#clientId),
      publishRange: (startMs, endMs, active) => this.#publishLoopRange(voiceId, startMs, endMs, active),
      publishEnable: (active) => this.#publishLoopEnable(voiceId, active),
      publishClear: () => this.#publishLoopRange(voiceId, null, null, false),
    });
  }

  /**
   * Apply repair snapshots held back while a deck was being driven. A deck still under the hand
   * keeps its snapshot and re-arms the refresh, so the repair lands as soon as the hand comes off
   * rather than being lost to a long gesture.
   */
  #drainDeferredStates() {
    const drained = new Set();
    if (this.#deferredStates.size === 0) return drained;
    let stillHeld = false;
    for (const [wireId, state] of [...this.#deferredStates]) {
      if (this.#editedRecently(wireId)) { stillHeld = true; continue; }
      this.#deferredStates.delete(wireId);
      this.#ingestWireState(wireId, state);
      drained.add(String(wireId));
    }
    if (stillHeld) this.#scheduleStateRefresh();
    return drained;
  }

  /** Did this client publish an edit to this deck within the snapshot-refresh window? */
  #editedRecently(wireId) {
    const at = this.#lastLocalEditAt.get(String(wireId));
    return at != null && this.#now() - at < STATE_REFRESH_DEBOUNCE_MS;
  }

  /** Is the deck a runtime voice sits on past its load barrier? (No wire mapping ⇒ not gated.) */
  #deckReadyForVoice(voiceId) {
    const wireId = this.#store.wireIdForVoice(voiceId);
    return wireId === null || !this.#deckLoads.has(String(wireId));
  }

  /**
   * Is there a way back INTO chat in this room?
   *
   * The rail hides its own trigger, so the only way in is the bubble in the score transport bar —
   * and that bar mounts for a room that is playing AND has a score, nothing else. Everywhere else a
   * closed rail cannot be reopened at all, which is why nothing may close it there. Derived from the
   * room's own state on every ask rather than latched, so it cannot drift from what is on screen.
   */
  #chatCanBeReopened() {
    if (this.#store.state.phase !== 'playing') return false;
    return Boolean(roomScore.current() && roomScore.sections());
  }

  #enterPlaying() {
    for (const voiceId of this.#voices.keys()) this.#enterPlayingForVoice(voiceId);
    this.#refreshVoiceCovers();
  }

  /** The room runs beat-locked: once playing, every voice joins the shared pulse. Applied as a
   *  REMOTE write — every client runs this for every voice it renders, so they already agree; letting
   *  it publish would fan N×M identical toggles and echo through the owner gate for a decision no
   *  user made. A holder who then steps their own voice off the pulse publishes that normally. */
  #enterPlayingForVoice(voiceId) {
    const deck = this.#registry.get(voiceId)?.deck;
    try { deck?.setSyncEnabled?.(true, { roomRemote: true }); } catch (error) {
      console.warn('[room] enabling sync for voice failed', voiceId, error);
    }
    // A room cannot honour launch grid 'none' — a shared start or seek has to name a boundary every
    // client agrees on. The pickers stop OFFERING it in a room; a deck carrying it from before is
    // moved to the default here, so what the menu shows is what the deck actually does.
    try {
      if (deck && !(deck.launchGridBars > 0)) {
        // Remember what the user actually chose: this override belongs to the room, and a deck that
        // outlives it must go back to 'none' rather than keep a bar grid nobody selected.
        if (!this.#preRoomLaunchGrid.has(voiceId)) this.#preRoomLaunchGrid.set(voiceId, deck.launchGridBars);
        // `roomRemote`: every client runs this for every voice it renders, so they already agree —
        // publishing would fan N×M identical writes for a grid no user picked (the same reason the
        // blanket sync-enable above is applied as a remote write).
        deck.setLaunchGridBars?.(DEFAULT_LAUNCH_GRID_BARS, { roomRemote: true });
      }
    } catch (error) {
      console.warn('[room] applying the room launch grid failed', voiceId, error);
    }
  }

  // ── local publish path ────────────────────────────────────────────────────

  #onLocalParam(voiceId, pm, name, dimensionId, metadata) {
    if (this.#applyingRemote || this.#disposed) return;
    if (metadata?.reason === 'subscribe') {
      // Not an edit — but it IS the moment this param became available to write to, which for a
      // deck attaching mid-boot is the moment a staged snapshot can finally land on it. A snapshot
      // that arrived before the voice hydrated had its levels dropped (there was nothing to apply
      // them to); without this the only thing that ever retries is the engine landing, so a deck
      // whose engine never assigns would keep its own level while the room believed it had adopted
      // the holder's. Re-entry is impossible: the apply below runs under the remote latch.
      if (this.#pendingStates.has(voiceId)) this.#tryApplyVoiceState(voiceId);
      return;
    }
    if (this.#store.state.phase !== 'playing') return;
    if (!this.#grantOrOwnHold(voiceId, name, dimensionId)) return;

    // `live` on the wire means A HAND IS ON THIS CONTROL — that is the only thing a lease is for.
    //
    // It is not the same as "a value is changing continuously". A Cosmic LFO, a phone's tilt and
    // the camera all push values many times a second and none of them is a hand: nobody is holding
    // that knob, so nobody should be kept off it. Reading their stream as a gesture would let the
    // first player to switch on an LFO take an axis away from everyone else for as long as it ran.
    //
    // A control writes through the ParamController it subscribed with (an object); input-router
    // streams pass their own id (a string) instead. But a string id alone doesn't make a
    // generator — that is the SOURCE's own declaration now, made where its id is minted at the
    // input-router seam and read back through `isGeneratorSource`. Pointer, wheel, keys and MIDI
    // all arrive by the object path — inbound MIDI drives the widget, so a MIDI knob is a hand
    // like any other.
    const fromHand = typeof metadata?.sourceController === 'object' && metadata.sourceController !== null;
    const intent = fromHand && metadata?.updateIntent !== 'commit' ? 'live' : 'commit';
    // A GENERATOR is a stream whose NEXT ABSOLUTE SAMPLE supersedes any rollback — a Cosmic LFO axis
    // or phone tilt. Continuous by nature and nobody's hand, so its stream is thinned like live
    // motion, marked on the wire so no ledger reads it as a gesture, and kept out of the hand-edit
    // bookkeeping (see #sendPlayerEvent) — an oscillator that never rests would otherwise hold the
    // snapshot refresh and every deferred repair off its deck for as long as it ran. The camera
    // fails that test — it writes deltas, not absolute samples, so it declares itself a hand at its
    // mint. A string tag nobody declared (e.g. a knob's commit write-back) is not a generator
    // either: it publishes as an ordinary unthinned commit with normal hand-edit bookkeeping.
    const generated = isGeneratorSource(metadata?.sourceController);

    // A gain stage is a single param on its own path — publish it WITHOUT touching the dimension
    // dedup or the axis bundle machinery (see ROUTED_PARAMS). It still thins continuous motion,
    // and a generator stream gets the same treatment here as on the dimensional path: thinned on
    // its own clock and marked on the envelope. Latent today (no generator drives a gain stage),
    // but the table promises one rule for every routed param, so this branch keeps that promise.
    const routed = ROUTED_PARAMS.get(name);
    if (routed && !routed.dimensional) {
      if (intent === 'live' || generated) {
        const sentAt = generated ? this.#lastGenSentAt : this.#lastLiveSentAt;
        const key = `${voiceId}/${name}/${NO_DIMENSION}`;
        const last = sentAt.get(key) ?? -Infinity;
        const t = this.#now();
        if (t - last < LIVE_PUBLISH_MIN_INTERVAL_MS) return;
        sentAt.set(key, t);
      }
      this.#publishGain(voiceId, name, pm.getNormalizedValue(name), intent, { generated });
      return;
    }

    const param = pm.getParameter?.(name);
    const dim = dimensionId ?? param?.activeDimensionId ?? NO_DIMENSION;

    if (metadata?.reason === 'active-dimension-change') {
      // Three axis subscribers hear the same switch — publish ONE bundle per new dimension.
      if (this.#lastPublishedDim.get(voiceId) === dim) return;
      this.#lastPublishedDim.set(voiceId, dim);
      this.#publishDimensionBundle(voiceId, dim, pm);
      return;
    }
    // A generator's writes carry whatever dimension they run on, not a dimension the USER moved
    // to — recording them here would mark a switch as already-published and swallow the one
    // bundle that actually announces it to the room.
    if (!generated) this.#lastPublishedDim.set(voiceId, dim);

    // Thin continuous motion; always send the final (commit) value. Thinning is safe for a lease
    // because the hold outlives many times this interval — but the commit must never be thinned, or
    // a gesture would only ever be released by its timeout. A generator stream has no gesture to
    // release — every sample is superseded by its next one — so it is thinned the same way, on its
    // own clock (see #lastGenSentAt).
    if (intent === 'live' || generated) {
      const sentAt = generated ? this.#lastGenSentAt : this.#lastLiveSentAt;
      const key = `${voiceId}/${name}/${dim}`;
      const last = sentAt.get(key) ?? -Infinity;
      const t = this.#now();
      if (t - last < LIVE_PUBLISH_MIN_INTERVAL_MS) return;
      sentAt.set(key, t);
    }
    const normalized = pm.getNormalizedValue(name, param?.isMultidimensional ? dim : null);
    this.#publishParam(voiceId, name, dim, normalized, intent, { generated });
  }

  #allowLocalWrite(voiceId, paramName, source, dimensionId = null) {
    if (!this.#store.isActive) return true;
    if (this.#applyingRemote || source === this) return true;
    if (source == null) return true; // system/boot writes carry no source controller
    if (source === SYNC_BPM_DISPLAY_SOURCE) return true; // the BPM readout repaint is a system write
    const phase = this.#store.state.phase;
    if (phase !== 'playing') return false; // the lobby is locked
    // One rule for every routed param: hold the voice, then win the lease — arbitrated at the
    // write's own dimension for dimensional params, at the param's own scope for the gain stages.
    const routed = ROUTED_PARAMS.get(paramName);
    if (routed) {
      return this.#grantOrOwnHold(voiceId, paramName, dimensionId)
        && this.#leaseAllows(voiceId, paramName, routed.dimensional ? dimensionId : null);
    }
    // Session tempo is writable by any holder (spectators excluded).
    if (paramName === SYNC_BPM_PARAM) return this.#store.ownedVoiceIds(this.#clientId).length > 0;
    // The audiofile-BPM readout: the value is ROUTED now (`/voice/<id>/native-tempo`), so the deck's
    // holder may drive it. Before routing, the gate dropped this write while the edit reached the
    // deck anyway through its own commit seam — the number snapped back while the grid had moved.
    if (paramName === SYNC_TRACK_BPM_PARAM) return this.#store.canEditVoice(voiceId, this.#clientId, paramName);
    // Coverage rule: an unrouted audible control may not stay live-but-local in a room.
    return false;
  }

  /** A section revocation leaves this client's already-open gesture alive until commit/expiry. */
  #grantOrOwnHold(voiceId, paramName, dimensionId = null) {
    if (this.#store.canEditVoice(voiceId, this.#clientId, paramName)) return true;
    let dim = dimensionId;
    if (dim == null) {
      const param = this.#registry.get(voiceId)?.parameterManager?.getParameter?.(paramName);
      dim = param?.isMultidimensional ? param.activeDimensionId ?? NO_DIMENSION : NO_DIMENSION;
    }
    return roomLeases.holderOf(voiceId, paramName, dim) === this.#clientId;
  }

  /**
   * Is this control free for us, or does someone else have a hand on it?
   *
   * The lock drawn on the control already stops a POINTER reaching a held knob, so in the ordinary
   * case this is never consulted. It exists for every other way a value moves: a MIDI controller,
   * the numeric keypad, phone tilt, a Cosmic LFO. None of those pass through a lock a user can see,
   * and letting them through would be exactly the fight the lease exists to end — with the added
   * insult of coming from a hand that isn't on the control.
   *
   * A hold belongs to one DIMENSION of a control, so the write has to say which one it is aimed at
   * rather than have this look up whichever is active. They differ more often than they sound like
   * they would: a keyboard ramp, a MIDI binding and a pointer drag all capture their dimension when
   * they start, and the room can switch the active one underneath. Arbitrating against the wrong
   * dimension both refuses writes nobody is contesting and lets through the ones that are.
   */
  #leaseAllows(voiceId, paramName, dimensionId = null) {
    let dim = dimensionId;
    if (dim == null) {
      const param = this.#registry.get(voiceId)?.parameterManager?.getParameter?.(paramName);
      dim = param?.isMultidimensional ? param.activeDimensionId ?? NO_DIMENSION : NO_DIMENSION;
    }
    return roomLeases.canWrite(voiceId, paramName, dim, this.#clientId);
  }

  /**
   * Wire every attached voice's Cosmic LFOs, and re-derive which of them this client DRIVES.
   *
   * Installing the bridge cannot be a one-shot at attach: a voice registers with the runtime fields
   * that exist at that instant, and its LFOs are built further down the same boot — so a deck that
   * loads into a room already in progress registers, is attached synchronously, and only THEN grows
   * a `cosmicLFOManager`. Bridged once and never retried, that deck's LFO would stay entirely
   * private, and — with no bridge to say otherwise — every client would run its own sweep on it,
   * which is the exact fight this routing exists to end. So it is idempotent and re-runs from every
   * hook that could follow the manager appearing: the attach, the engine landing, and any store
   * change. `#cosmicBridged` is what keeps it to one install per voice.
   */
  #refreshCosmicWiring() {
    this.#cosmicGenerators.clear();
    for (const [voiceId, rec] of this.#voices) {
      const wireId = this.#store.wireIdForVoice(voiceId);
      if (wireId === null) continue;
      // Only the deck's speaker drives its oscillators. Nobody holding the deck means nobody may
      // write its axes at all (the gate refuses every client), so nobody generates either — running
      // an oscillator whose every value is dropped is N rAF loops bought for nothing.
      if (this.#store.speakerForWire(wireId) === this.#clientId) this.#cosmicGenerators.add(voiceId);
      if (this.#cosmicBridged.has(voiceId)) continue;
      const manager = rec.ctx?.cosmicLFOManager;
      if (!manager) continue; // built later in this deck's boot — a following hook picks it up
      for (const axis of COSMIC_AXES) {
        manager[axis]?.setRoomBridge?.({
          canControl: () => this.#store.canEditVoice(voiceId, this.#clientId),
          canGenerate: () => this.#cosmicGenerators.has(voiceId),
          publishEnable: (on) => this.#publishCosmic(voiceId, axis, 'enable', on ? 1 : 0),
          publishSource: (key) => this.#publishCosmic(voiceId, axis, 'source', key),
          publishWaveform: (key) => this.#publishCosmic(voiceId, axis, 'waveform', key),
          publishKick: (step) => this.#publishCosmic(voiceId, axis, 'kick', step),
          publishNoteRate: (key) => this.#publishCosmic(voiceId, axis, 'rate', key),
        });
      }
      this.#cosmicBridged.add(voiceId);
    }
  }

  /**
   * Make the device-sensor toggle one shared button for as long as this room lasts.
   *
   * Installed ONCE, on the class rather than per voice, because that is the shape of the thing: one
   * sensor controller per client — one phone in one hand — following whichever deck that player is
   * on. So the deck a press is about is not a parameter of the bridge; it is read at press time,
   * and a press while focused on no room deck is simply not a room act. Holding it on the class
   * also covers the controller being built lazily, on first use of the Sensors panel: the seam is
   * waiting for it whenever that happens, mid-room or not.
   *
   * `canControl` is the ordinary edit gate. There is no `canGenerate` counterpart: who drives is not
   * derived from grants but settled by the press itself, since a sensor is somebody's own device and
   * only the person holding it can put it on the axis.
   */
  #installSensorBridge() {
    setRoomSensorPublishers({
      publish: (voiceId, axis, on) => this.#publishSensor(voiceId, axis, on),
      canEdit: (voiceId) => this.#store.canEditVoice(voiceId, this.#clientId),
      // The one deck this client's own device can reach: a single controller follows the focused
      // voice. Every OTHER deck's switch is still shared and still shown — there is simply nobody
      // here to drive it.
      focusedVoice: () => this.#registry.activeId ?? null,
      selfId: () => this.#clientId,
      // Pressing a deck's toggle means driving that deck, so the press focuses it first.
      // …unless the deck is already selected: the gang reaches it, and a plain focus would collapse
      // the selection the player built.
      focus: (voiceId) => {
        if (!this.#registry.isSelected?.(voiceId)) this.#registry.setActive?.(voiceId);
      },
      // Which dimension a deck is playing is NOT overridden: the store answers it from the deck's
      // world mode, solo and in a room alike. A second answer here (the x param's) keyed the same
      // switch two ways, and a switch pressed in a room went dark the moment the room ended.
      device: ({ create }) => (create
        ? this.#sensorController?.getInstance?.(
          this.#registry.get(this.#registry.activeId)?.parameterManager ?? null,
        ) ?? null
        : this.#sensorController?.getExistingInstance?.() ?? null),
    });
  }

  // ── non-owned voice covers ────────────────────────────────────────────────

  /** Pointer-blocking cover over every tile this client may not edit (spectator: all of them). */
  #refreshVoiceCovers() {
    if (typeof document === 'undefined') return;
    for (const [voiceId, rec] of this.#voices) {
      const host = typeof Element !== 'undefined' && rec.ctx.themeRoot instanceof Element
        ? rec.ctx.themeRoot
        : null;
      const locked = this.#store.isActive && !this.#store.canEditVoice(voiceId, this.#clientId);
      if (!host) continue;
      if (locked && !rec.cover) {
        const cover = document.createElement('div');
        cover.setAttribute('data-room-locked', 'true');
        cover.setAttribute('aria-hidden', 'true');
        Object.assign(cover.style, {
          position: 'absolute',
          inset: '0',
          zIndex: '60',
          pointerEvents: 'auto',
          cursor: 'not-allowed',
          background: 'transparent',
        });
        host.appendChild(cover);
        rec.cover = cover;
      } else if (!locked && rec.cover) {
        rec.cover.remove();
        rec.cover = null;
      }
    }
  }
}

// ── module wiring (one client per tab, keyed by room) ───────────────────────

let activeClient = null; // { roomId, client }

/**
 * Build (once) the tab's room client. Idempotent per room id; a different room replaces the old
 * client. Called from the sync boot when a multi realm carries a room param.
 * @param {{ adapter: *, roomId: string, voiceOrder: Array<string|null>, voiceCount?: (number|null),
 *   create?: ({ players: (number|null), grantId: (string|null), rosterUrl: (string|null),
 *     score?: (object|null), draw?: (object|null), pinned?: (Array<{id:string,kind:string}>|null) } | null) }} opts
 *   `voiceCount` is the fixed deck count (mint shape); `create` (creator's tab only) mints the room
 *   before joining; omit/null to only join.
 * @returns {RoomClient}
 */
export function initRoomClient({ adapter, roomId, voiceOrder = [], voiceCount = null, create = null }) {
  if (activeClient?.roomId === roomId) return activeClient.client;
  activeClient?.client?.dispose?.();
  const client = new RoomClient({ adapter, roomId, voiceOrder, voiceCount, create });
  activeClient = { roomId, client };
  // The waiting room (who is here, the invite, the creator's Start). Lazy so non-room boots never
  // load it; failures are non-fatal (the room still functions without the lobby chrome).
  if (typeof document !== 'undefined') {
    // Saying that somebody arrived or left. Started HERE, not in the waiting room, because a
    // departure has to be announced during the session too — and by then the waiting room is
    // long gone. It stays silent until the waiting room tells it otherwise.
    import('./roomPresence.js')
      .then((m) => m.startRoomPresence())
      .catch((error) => console.warn('[room] presence announcer failed to start', error));
    import('./mountWaitingRoom.tsx')
      .then((m) => m.mountWaitingRoom())
      .catch((error) => console.warn('[room] waiting room failed to mount', error));
    // The room chat rail (collapsed by default; unread badge + a peeked line while closed).
    // Same lazy pattern: chat chrome failing must never take the room down with it.
    import('./chat/mountRoomChatPanel.tsx')
      .then((m) => m.mountRoomChatPanel())
      .catch((error) => console.warn('[room] chat panel failed to mount', error));
  }
  return client;
}

/** Tear down the tab's room client (a re-initialization without a room must not stay room-locked). */
export function disposeRoomClient() {
  activeClient?.client?.dispose?.();
  activeClient = null;
  // The people we looked up belonged to THAT room. Dropping them keeps a tab that hops from room
  // to room from hoarding identities, and means the next room re-reads anyone still relevant.
  void import('./userCards.js').then((m) => m.resetUserCards()).catch(() => {});
}

/** Republish the live positional stage→voice map to the tab's room client (no-op outside room mode). */
export function updateRoomVoiceOrder(order) {
  activeClient?.client?.setVoiceOrder?.(order);
}

/** Does the local client own deck `stageIndex` right now? False outside room mode. */
export function roomOwnsDeck(stageIndex) {
  return activeClient?.client?.ownsDeck?.(stageIndex) ?? false;
}

/** The tab's live room client, or null outside room mode (read seam for UI surfaces). */
export function getRoomClient() {
  return activeClient?.client ?? null;
}
